import React, { useState, useEffect, useRef } from 'react';
import { recordProductOpened } from './productActivity';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import { broadcastRevalidate } from './sessionRevocation';
import ProductNav from './components/ProductNav';
import SideHelpPanel from './components/SideHelpPanel';
import HelpEntry from './help/HelpEntry';
import { usesGuidedHelp } from './help/helpManifest';
import DccVerificationOverlay from './DccVerificationOverlay';
import BattlemapsDashboard from './BattlemapsDashboard';
import './portalResponsive.css';
import {
  PRODUCTS, LAST_PRODUCT_KEY,
  getProductFromRoute,
} from './config/products';
import { CATEGORY_PALETTES, FOUNDATION } from './config/categoryTheme';
import { glassPill, glassSurface } from './config/glassTheme';
import {
  getCombatProOwned, COMBAT_TIERS, getTierSpec,
  viOpenCombatPricing, viOpenCombatMembership, viOpenCombatPurchaseHistory,
} from './combatProTiers';

// Standard VISINVOKE product header text (title + subtitle), keyed by product.
const PRODUCT_HEADERS = {
  visual:        { title: 'D&D Image Generators',    subtitle: 'Fantasy, RPG, and game-based image generation.' },
  professional:  { title: 'Professional Generators', subtitle: 'Business, marketing, and commercial image generation.' },
  battlemaps:    { title: 'Battlemaps',              subtitle: 'Tactical maps, terrain systems, structures, props, and cinematic scenes.' },
  battletracker: { title: 'Combat PRO',              subtitle: 'All in One DND combat and encounter management system.' },
};

// ── Premium dark surface system (visual refresh) ──────────────────────────────
// Each image-generator dashboard shares one dark, layered visual language while
// carrying its own restrained product-colour atmosphere. Per-product surface
// bases plus an accent derived from product.color are published as CSS custom
// properties on the page wrapper, so the shared style object can tint every
// panel without any layout, structural or workflow change.
// Per-category dark surface system, derived from the authoritative bright
// category palette:
//   s1 = category "Dark surface tint"      (standard/major panel surface)
//   s2 = category "Secondary dark tint"    (nested/secondary panel surface)
//   pg = the genuinely dark global foundation (#080B11) for every category
// battletracker (Combat PRO) is now included, so it no longer falls back to
// the D&D purple surfaces.
const VI_SURFACE_BASE = Object.keys(CATEGORY_PALETTES).reduce((acc, k) => {
  const p = CATEGORY_PALETTES[k];
  acc[k] = { s1: p.surface, s2: p.surfaceDark, pg: FOUNDATION.bg };
  return acc;
}, {});

function viHexToRgb(hex) {
  var h = (hex || '#A347FF').replace('#', '');
  if (h.length === 3) { h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]; }
  var r = parseInt(h.substring(0, 2), 16);
  var g = parseInt(h.substring(2, 4), 16);
  var b = parseInt(h.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) { return '163, 71, 255'; }
  return r + ', ' + g + ', ' + b;
}

function viThemeVars(product) {
  var base = VI_SURFACE_BASE[product.key] || VI_SURFACE_BASE.visual;
  var pal = (product && product.palette) || CATEGORY_PALETTES[product.key] || CATEGORY_PALETTES.visual;
  return {
    '--vi-acc': viHexToRgb(product.color),
    '--vi-s1': base.s1,
    '--vi-s2': base.s2,
    '--vi-pg': base.pg,
    // Category identity available to shared style objects so no page falls back
    // to generic purple: the category CTA gradient, its hover, primary and text.
    '--vi-primary': pal.primary,
    '--vi-primary-rgb': viHexToRgb(pal.primary),
    '--vi-deep-rgb': viHexToRgb(pal.deep),
    '--vi-bright-rgb': viHexToRgb(pal.bright),
    '--vi-text': pal.text,
    '--vi-border': pal.border,
    '--vi-cta': pal.cta,
    '--vi-cta-hover': pal.ctaHover,
  };
}

function viPageBg(product) {
  var acc = viHexToRgb(product.color);
  // Subtle atmospheric version of the category colour over the genuinely dark
  // foundation. Most visible around the upper page/header region, fading into
  // near-black. The base is NOT a solid coloured background.
  return 'radial-gradient(1200px 640px at 50% -4%, rgba(' + acc + ', 0.09) 0%, rgba(0,0,0,0) 44%), ' +
         'radial-gradient(1000px 720px at 100% 112%, rgba(' + acc + ', 0.05) 0%, rgba(0,0,0,0) 55%), ' +
         FOUNDATION.bg;
}

const VG_CAT = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.07) 32%, rgba(255,255,255,0) 55%), linear-gradient(180deg, rgba(var(--vi-primary-rgb),0.72) 0%, rgba(var(--vi-primary-rgb),0.30) 44%, rgba(var(--vi-deep-rgb),0.16) 74%, rgba(9,11,17,0) 100%), linear-gradient(180deg, #14171f 0%, #090b11 100%)',
  border: '1.5px solid rgba(var(--vi-bright-rgb),0.9)',
  boxShadow: '0 0 18px rgba(var(--vi-primary-rgb),0.75), 0 0 40px rgba(var(--vi-primary-rgb),0.4), inset 0 0 22px rgba(var(--vi-primary-rgb),0.26), inset 0 1px 0 rgba(255,255,255,0.28), 0 8px 22px rgba(0,0,0,0.5)',
  color: '#fff',
  textShadow: '0 0 10px rgba(var(--vi-bright-rgb),0.85), 0 1px 4px rgba(0,0,0,0.6)',
};
function vgGlass(d, p, br) {
  var rp = viHexToRgb(p), rd = viHexToRgb(d), rb = viHexToRgb(br);
  return {
    background: 'linear-gradient(180deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.07) 32%, rgba(255,255,255,0) 55%), linear-gradient(180deg, rgba(' + rp + ',0.72) 0%, rgba(' + rp + ',0.30) 44%, rgba(' + rd + ',0.16) 74%, rgba(9,11,17,0) 100%), linear-gradient(180deg, #14171f 0%, #090b11 100%)',
    border: '1.5px solid rgba(' + rb + ',0.9)',
    boxShadow: '0 0 18px rgba(' + rp + ',0.75), 0 0 40px rgba(' + rp + ',0.4), inset 0 0 22px rgba(' + rp + ',0.26), inset 0 1px 0 rgba(255,255,255,0.28), 0 8px 22px rgba(0,0,0,0.5)',
    color: '#fff',
    textShadow: '0 0 10px rgba(' + rb + ',0.85), 0 1px 4px rgba(0,0,0,0.6)',
  };
}
function vgBarStyle(p, br, d) {
  var rp = viHexToRgb(p), rbr = viHexToRgb(br || p), rd = viHexToRgb(d || p);
  return {
    background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(' + rbr + ',0.95) 38%, rgba(' + rp + ',1) 60%, rgba(' + rd + ',1) 100%)',
    boxShadow: '0 0 8px rgba(' + rp + ',0.95), 0 0 16px rgba(' + rp + ',0.6), inset 0 1px 0 rgba(255,255,255,0.55)',
  };
}
function vgBar(p, d) {
  var rp = viHexToRgb(p), rd = viHexToRgb(d || p);
  return 'linear-gradient(180deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.08) 42%, rgba(' + rp + ',0.95) 43%, rgba(' + rd + ',0.98) 100%)';
}
const DASHBOARD_BTN_CSS = `

  /* ── SECTION 1: Ecosystem card hover (Level 1 — strongest) ── */
  .vi-eco-dnd:hover     { box-shadow: 0 18px 48px rgba(0,0,0,0.28), 0 0 28px rgba(163,71,255,0.28) !important; }
  .vi-eco-prof:hover    { box-shadow: 0 18px 48px rgba(0,0,0,0.28), 0 0 28px rgba(24,189,242,0.27) !important; }
  .vi-eco-bm:hover      { box-shadow: 0 18px 48px rgba(0,0,0,0.28), 0 0 28px rgba(245,154,35,0.27) !important; }
  .vi-eco-combat:hover  { box-shadow: 0 18px 48px rgba(0,0,0,0.28), 0 0 28px rgba(25,199,133,0.27) !important; }
  .vi-eco-rpg:hover     { box-shadow: 0 18px 48px rgba(0,0,0,0.28), 0 0 28px rgba(229,59,99,0.27) !important; }
  .vi-eco-util:hover    { box-shadow: 0 18px 48px rgba(0,0,0,0.28), 0 0 28px rgba(47,125,255,0.27) !important; }

  .vi-ecosystem-card-hover {
    transition: transform 220ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 220ms cubic-bezier(0.22,1,0.36,1),
                border-color 220ms ease,
                filter 220ms ease !important;
  }
  .vi-ecosystem-card-hover:hover {
    transform: translateY(-6px) !important;
    filter: brightness(1.04) !important;
  }

  /* ── SECTION 2: Navigation (Level 2) ── */
  .vi-product-tab {
    transition: all 180ms cubic-bezier(0.22,1,0.36,1) !important;
  }
  .vi-product-tab:hover {
    transform: translateY(-2px) !important;
    filter: brightness(1.08) !important;
  }
  .vi-product-tab:active {
    transform: translateY(0px) !important;
  }

  /* ── SECTION 3: Recent build cards (Level 1) ── */
  .vi-build-card {
    transition: transform 220ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 220ms cubic-bezier(0.22,1,0.36,1) !important;
  }
  .vi-build-card:hover {
    transform: translateY(-5px) !important;
    box-shadow: 0 16px 44px rgba(0,0,0,0.24) !important;
  }
  .vi-build-img {
    transition: transform 320ms cubic-bezier(0.22,1,0.36,1),
                filter 320ms ease !important;
    opacity: 0;
    animation: vi-img-fade 420ms ease 80ms forwards;
  }
  .vi-build-img:hover {
    transform: scale(1.03) !important;
    filter: brightness(1.06) !important;
  }
  @keyframes vi-img-fade {
    to { opacity: 1; }
  }

  /* ── SECTION 4: Dashboard load reveals ── */
  .vi-reveal {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 420ms cubic-bezier(0.22,1,0.36,1),
                transform 420ms cubic-bezier(0.22,1,0.36,1);
  }
  .vi-reveal.vi-visible {
    opacity: 1;
    transform: translateY(0);
  }
  .vi-reveal-d1 { transition-delay: 0ms; }
  .vi-reveal-d2 { transition-delay: 80ms; }
  .vi-reveal-d3 { transition-delay: 160ms; }
  .vi-reveal-d4 { transition-delay: 240ms; }
  .vi-reveal-d5 { transition-delay: 320ms; }

  /* ── SECTION 5: Help panel ── */
  .vi-help-panel-enter {
    animation: vi-help-slide 260ms cubic-bezier(0.22,1,0.36,1) both;
  }
  @keyframes vi-help-slide {
    from { transform: translateX(18px); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
  .vi-help-item {
    transition: background 160ms ease !important;
    border-radius: 6px;
  }
  .vi-help-item:hover {
    background: rgba(255,255,255,0.06) !important;
  }

  /* ── SECTION 6: Live dashboard feel ── */
  @keyframes vi-bar-grow {
    from { width: 0 !important; }
  }
  .vi-bar-live {
    animation: vi-bar-grow 900ms cubic-bezier(0.22,1,0.36,1) both;
  }

  @keyframes vi-pulse-active {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.82; }
  }
  .vi-status-active {
    animation: vi-pulse-active 2.6s ease-in-out infinite;
  }

  /* ── SECTION 7: Button system (Level 1) ── */
  .vi-btn-primary {
    transition: transform 160ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 180ms ease,
                filter 180ms ease !important;
  }
  .vi-btn-primary:hover {
    transform: translateY(-3px) !important;
    filter: brightness(1.08) !important;
    box-shadow: 0 12px 32px rgba(0,0,0,0.22) !important;
  }
  .vi-btn-primary:active {
    transform: translateY(1px) !important;
    filter: brightness(0.94) !important;
  }

  /* Secondary buttons */
  .vi-dash-primary:hover { filter: brightness(1.1); transform: translateY(-3px); box-shadow: 0 10px 28px rgba(var(--vi-acc, 163,71,255),0.45) !important; }
  .vi-dash-primary:active { filter: brightness(0.92); transform: translateY(1px); }
  .vi-dash-secondary:hover { background: #e9eaf0 !important; transform: translateY(-2px); }
  .vi-dash-secondary:active { transform: translateY(1px); }
  .vi-dash-continue-recent:hover { filter: brightness(1.15); transform: translateY(-3px); box-shadow: 0 10px 28px rgba(var(--vi-acc, 163,71,255),0.45) !important; }
  .vi-dash-continue-recent:active { filter: brightness(0.88); transform: translateY(1px); }

  /* ── Anim button ── */
  .vi-anim-btn {
    transition: filter 0.16s ease, transform 0.14s cubic-bezier(0.22,1,0.36,1), box-shadow 0.16s ease !important;
  }
  .vi-anim-btn:hover {
    filter: brightness(1.14) !important;
    transform: translateY(-3px) !important;
    box-shadow: 0 10px 24px rgba(21,128,61,0.5) !important;
  }
  .vi-anim-btn:active {
    transform: translateY(1px) !important;
  }

  /* ── Quick links ── */
  .vi-quicklink-hover {
    transition: background 160ms ease, transform 180ms cubic-bezier(0.22,1,0.36,1) !important;
    cursor: pointer;
  }
  .vi-quicklink-hover:hover {
    transform: translateX(4px) !important;
    background: rgba(var(--vi-acc, 163, 71, 255), 0.12) !important;
    border-color: rgba(var(--vi-acc, 163, 71, 255), 0.32) !important;
  }

  /* ── General card hover ── */
  .vi-card-hover {
    transition: transform 220ms cubic-bezier(0.22,1,0.36,1),
                box-shadow 220ms cubic-bezier(0.22,1,0.36,1) !important;
  }
  .vi-card-hover:hover {
    transform: translateY(-3px) !important;
    box-shadow: 0 18px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(var(--vi-acc, 163, 71, 255), 0.28) !important;
  }

  /* ── SECTION 8: Background atmosphere ── */
  @keyframes vi-atmo-drift {
    0%   { transform: translate(-50%, -50%) scale(1);    opacity: 0.22; }
    50%  { transform: translate(-48%, -52%) scale(1.06); opacity: 0.28; }
    100% { transform: translate(-50%, -50%) scale(1);    opacity: 0.22; }
  }
  .vi-atmo-glow {
    position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden;
  }
  .vi-atmo-glow::after {
    content: '';
    position: absolute;
    top: 50%; left: 50%;
    width: 800px; height: 600px;
    border-radius: 50%;
    background: radial-gradient(ellipse, rgba(var(--vi-acc, 163, 71, 255), 0.06) 0%, transparent 65%);
    transform: translate(-50%,-50%);
    animation: vi-atmo-drift 22s ease-in-out infinite alternate;
  }

  /* ── Card fade stagger ── */
  .vi-fade-up-1 { animation: vi-fade-up 0.38s cubic-bezier(0.22,1,0.36,1) 0.04s both; }
  .vi-fade-up-2 { animation: vi-fade-up 0.38s cubic-bezier(0.22,1,0.36,1) 0.12s both; }
  .vi-fade-up-3 { animation: vi-fade-up 0.38s cubic-bezier(0.22,1,0.36,1) 0.20s both; }
  .vi-fade-up-4 { animation: vi-fade-up 0.38s cubic-bezier(0.22,1,0.36,1) 0.28s both; }
  .vi-fade-up-5 { animation: vi-fade-up 0.38s cubic-bezier(0.22,1,0.36,1) 0.36s both; }
  @keyframes vi-fade-up {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const VI_LAYER_KEYS = {
  's1-title':  ['primarySubject','species','roleArchetype','factionAlignment','originBackground','rarityStatus','ageTier','genderExpression','conceptualContext','styleIntensity','entityCount','narrativeTone'],
  'sx-title':  ['constructionMaterials','surfaceFinish','technologyBaseline','condition','arrangement','l2Pose','l2Stance','l2BodyType','l2Scale','l2Condition','l2Movement','l2LimbPos','l2Orientation','l2Formation','l2Grip','l2UpperBody','l2Spacing','l2Mood','l2Facial','l2Outfit','l2Material','l2OutfitStyle','l2ClothingColour','l2WeaponType','l2WeaponDetail','l2WeaponColour','l2WeaponUsage','l2CompanionType','l2CompanionDetail','l2CompanionQty','l2Interaction','primaryModifiers','secondaryModifiers','artObjects','special','structuralElements','fastenerDetails','eraExclusions'],
  's2-title':  ['presenceLevel','activityState'],
  's25-title': ['advFlavour'],
  's3a-title': ['avoid'],
  's3-title':  ['renderStyle','camera','framing','detailIntensity','scale','lighting','mood','lensAndPerspective','subjectEmphasis','depthOfField','colourPalette','atmosphereEffects'],
};

// ── Stage 8D: Go target mappings ──────────────────────────────────────────────
// Maps hint strings and layer numbers to {layer, control} for visinvoke_go_target
const GO_TARGET_BY_HINT = {
  subject:     { layer: 's1-title',  control: 'l1_left_primary_subject' },
  environment: { layer: 's25-title', control: 'l4_environment_type' },
  state:       { layer: 'sx-title',  control: 'l2_stance' },
};

const GO_TARGET_BY_LAYER = {
  1: { layer: 's1-title',  control: 'l1_left_primary_subject' },
  2: { layer: 'sx-title',  control: 'l2_stance' },
  4: { layer: 's25-title', control: 'l4_setting_context' },
};

// ── Stage 7: Build Evaluation ─────────────────────────────────────────────────
function evaluateBuild(cfg) {
  if (!cfg || typeof cfg !== 'object') {
    return { primaryStrength: 'weak', environmentStrength: 'weak', stateClarity: 'unclear', conflictLevel: 'none' };
  }

  function hasVal(k) {
    const v = cfg[k];
    if (!v) return false;
    if (Array.isArray(v)) return v.length > 0;
    return typeof v === 'string' && v.trim() !== '';
  }

  // Primary strength
  const hasPrimary = hasVal('primarySubject') || hasVal('species') || hasVal('roleArchetype');
  const hasCompanion = hasVal('l2CompanionType') || hasVal('l2CompanionDetail');
  let primaryStrength;
  if (!hasPrimary) {
    primaryStrength = 'weak';
  } else if (hasPrimary && hasCompanion) {
    primaryStrength = 'medium';
  } else {
    primaryStrength = 'strong';
  }

  // Environment strength
  const hasEnvType = hasVal('environmentType');
  const hasEnvCtx  = hasVal('settingContext');
  let environmentStrength;
  if (hasEnvType && hasEnvCtx) environmentStrength = 'strong';
  else if (hasEnvType || hasEnvCtx) environmentStrength = 'medium';
  else environmentStrength = 'weak';

  // State clarity
  const hasMovement = hasVal('l2Movement');
  const hasStance   = hasVal('l2Stance');
  let stateClarity;
  if (hasMovement || hasStance) {
    const mv = (cfg.l2Movement || '').toLowerCase();
    const st = (cfg.l2Stance || '').toLowerCase();
    const vague = /^(default|normal|standard|none|neutral)$/.test(mv + st);
    stateClarity = vague ? 'partial' : 'clear';
  } else {
    stateClarity = 'unclear';
  }

  // Conflict level
  const envType    = (cfg.environmentType || '').toLowerCase();
  const settingCtx = (cfg.settingContext || '').toLowerCase();
  let conflictLevel = 'none';
  if (envType && settingCtx) {
    const biomeConflicts = [
      ['desert', 'tundra'], ['desert', 'arctic'], ['desert', 'snow'],
      ['ocean', 'mountain'], ['underwater', 'desert'],
    ];
    for (const [a, b] of biomeConflicts) {
      if ((envType.includes(a) && settingCtx.includes(b)) ||
          (envType.includes(b) && settingCtx.includes(a))) {
        conflictLevel = 'high';
        break;
      }
    }
    if (conflictLevel === 'none' && envType && settingCtx && envType !== settingCtx) {
      conflictLevel = 'low';
    }
  }

  return { primaryStrength, environmentStrength, stateClarity, conflictLevel };
}

function getBuildSignal(evaluation) {
  const { primaryStrength, environmentStrength, conflictLevel } = evaluation;
  if (conflictLevel === 'high') return 'red';
  if (primaryStrength === 'strong' && environmentStrength !== 'weak') return 'green';
  if (primaryStrength === 'weak') return 'red';
  return 'yellow';
}

function getBuildMessages(evaluation) {
  const msgs = [];
  if (evaluation.primaryStrength === 'weak') msgs.push({ label: 'Primary subject may not be dominant', reason: 'No clear primary subject is defined for this build', source: 'Layer 1', layer: 1 });
  else if (evaluation.primaryStrength === 'medium') msgs.push({ label: 'Companion present — subject focus may be reduced', reason: 'A companion is defined alongside the primary subject', source: 'Layer 2', layer: 1 });
  if (evaluation.environmentStrength === 'weak') msgs.push({ label: 'Environment not clearly defined', reason: 'Recent builds are missing strong environment context', source: 'Layer 4', layer: 4 });
  if (evaluation.stateClarity === 'unclear') msgs.push({ label: 'State not clearly expressed', reason: 'Character or subject state lacks clarity across builds', source: 'Layer 2', layer: 2 });
  if (evaluation.conflictLevel === 'high') msgs.push({ label: 'Conflicting environment inputs detected', reason: 'Selected environments may produce contradictory results', source: 'Layer 4', layer: 4 });
  return msgs.slice(0, 2);
}

// ── Stage 8: Conversion Intelligence ─────────────────────────────────────────
function detectMomentum(recentJobs) {
  if (!Array.isArray(recentJobs) || recentJobs.length < 3) return false;
  const completed = recentJobs.filter(j => j.status === 'completed');
  if (completed.length >= 3) {
    const newest = new Date(completed[0].completed_at || completed[0].created_at);
    const oldest = new Date(completed[2].completed_at || completed[2].created_at);
    const diffHours = Math.abs(newest - oldest) / 36e5;
    if (diffHours <= 24) return true;
  }
  const hasVariations = recentJobs.some(j => (j.variant_count || 0) >= 2);
  return hasVariations;
}

function detectComplexBuild(recentJobs) {
  if (!Array.isArray(recentJobs) || recentJobs.length === 0) return false;
  const completed = recentJobs.filter(j => j.status === 'completed' && j.configuration_parameters);
  for (const job of completed.slice(0, 3)) {
    const ev = evaluateBuild(job.configuration_parameters || {});
    if (ev.primaryStrength === 'strong' && ev.environmentStrength !== 'weak') return true;
  }
  return false;
}

function buildWorkspaceHint(recentJobs) {
  if (!Array.isArray(recentJobs) || recentJobs.length === 0) return null;
  const completed = recentJobs.filter(j => j.status === 'completed' && j.configuration_parameters).slice(0, 3);
  if (completed.length === 0) return null;
  let strongCount = 0, missingEnv = 0, missingState = 0;
  completed.forEach(job => {
    const ev = evaluateBuild(job.configuration_parameters || {});
    if (ev.primaryStrength === 'strong' && ev.environmentStrength !== 'weak') strongCount++;
    if (ev.environmentStrength === 'weak') missingEnv++;
    if (ev.stateClarity === 'unclear') missingState++;
  });
  if (strongCount >= 2) return 'Recent builds show increasing clarity and iteration — more capacity enables deeper refinement.';
  if (missingEnv >= 2 || missingState >= 2) return 'Recent builds show partial structure — refining environment and state will improve results.';
  if (strongCount >= 1) return 'Recent builds show increasing clarity and iteration — more capacity enables deeper refinement.';
  return null;
}

// ── Variation modifiers ───────────────────────────────────────────────────────
const VARIATION_MODIFIERS = {
  standard:    '',
  composition: 'stronger composition emphasis',
  cinematic:   'cinematic lighting, dramatic framing',
  clean:       'minimal clutter, clean scene',
};

function viDetectIntent(job) {
  const cfg = job.configuration_parameters || {};
  const variantCount = job.variant_count || 0;
  const allKeys = Object.values(VI_LAYER_KEYS).flat();
  const totalFields = allKeys.filter(k => { const v = cfg[k]; if (!v) return false; if (Array.isArray(v)) return v.length > 0; return typeof v === 'string' && v.trim() !== ''; }).length;
  if (variantCount >= 2) return 'variation';
  const layersCovered = Object.entries(VI_LAYER_KEYS).filter(([, keys]) => keys.some(k => { const v = cfg[k]; if (!v) return false; if (Array.isArray(v)) return v.length > 0; return typeof v === 'string' && v.trim() !== ''; })).length;
  if (layersCovered >= 4 || totalFields >= 10) return 'exploration';
  return 'refinement';
}

function viDetectLastLayer(cfg) {
  if (!cfg || typeof cfg !== 'object') return 's1-title';
  let best = 's1-title', bestCount = 0;
  Object.entries(VI_LAYER_KEYS).forEach(([id, keys]) => {
    const count = keys.filter(k => { const v = cfg[k]; if (!v) return false; if (Array.isArray(v)) return v.length > 0; return typeof v === 'string' && v.trim() !== ''; }).length;
    if (count > bestCount) { bestCount = count; best = id; }
  });
  return best;
}

function viIntentLabel(job) {
  const intent = viDetectIntent(job);
  if (intent === 'variation') return 'Generate New Variation';
  if (intent === 'exploration') return 'Continue Exploring';
  return 'Continue Build';
}

function viWriteRehydrate(job, isVariation) {
  try {
    const slug = job.generator_slug;
    let configuration = null;
    try {
      const saved = localStorage.getItem('visinvoke_state_' + slug);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.state && Object.keys(parsed.state).length > 0) {
          configuration = parsed.state;
        }
      }
    } catch (e) {
      console.warn('VISINVOKE: localStorage read failed', e);
    }
    if (!configuration) {
      if (job.full_state && Object.keys(job.full_state).length > 0) {
        configuration = job.full_state;
      }
    }
    if (!configuration) {
      console.warn('VISINVOKE: Falling back to filtered configuration_parameters');
      configuration = job.configuration_parameters || {};
    }
    const payload = {
      slug: slug,
      configuration: configuration,
    };
    sessionStorage.setItem('vi_build', JSON.stringify(payload));
  } catch(e) {}
}

function loadVersion(job, versionIndex) {
  const version = job.versions?.[versionIndex];
  if (!version || !version.state) return;
  const configuration = version.state;
  sessionStorage.setItem('vi_build', JSON.stringify({
    configuration,
    slug: job.generator_slug
  }));
  window.location.href = '/engine';
}

function isValidLastBuild(job) {
  if (!job) return false;
  if (job.status !== 'completed') return false;
  const slug = job.generator_slug;
  if (!slug || typeof slug !== 'string' || !slug.trim()) return false;
  if (!job.result || !job.result.url || !job.result.url.trim()) return false;
  return true;
}

function resolveLastBuildDestination(job) {
  const slug = job.generator_slug?.trim();
  if (!slug) return null;
  return { route: '/engine', slug };
}

function resolveProduct(pathname) {
  const fromRoute = getProductFromRoute(pathname);
  if (fromRoute) return fromRoute;
  const last = localStorage.getItem(LAST_PRODUCT_KEY);
  if (last && PRODUCTS[last]) return PRODUCTS[last];
  return PRODUCTS.visual;
}

const ECOSYSTEM_SYSTEMS = [
  { key: 'dnd', icon: '🐉', title: 'D&D Generators', desc: 'Creative generation system. Deep layered configuration for fantasy and worldbuilding image creation.', points: ['8,000+ generators available', 'Up to 4,000+ structured selections and up to 38 custom-configured inputs', 'Maximum creative variation and control', 'Designed for exploration and worldbuilding'], cta: 'Open D&D Generators', route: '/generators', color: '#A347FF', gradient: 'linear-gradient(135deg, #A347FF, #B966FF)', live: true },
  { key: 'professional', icon: '💼', title: 'Professional Generators', desc: 'Structured production system. Refined configuration paths for consistent, repeatable professional output.', points: ['8,000+ generators available', 'Up to 4,000+ structured selections and up to 38 custom-configured inputs', 'Built for production workflows and ongoing projects', 'Reduced noise, increased precision'], cta: 'Open Professional Generators', route: '/generators?library=professional', color: '#18BDF2', gradient: 'linear-gradient(135deg, #18BDF2, #3BD3FF)', live: true },
  { key: 'battlemaps', icon: '🗺️', title: 'Battlemaps', desc: 'Tactical map generation system. Grid-aligned, VTT-ready outputs for rapid encounter deployment.', points: ['Tactical maps, assets, and environment generation', 'Grid-aligned, VTT-ready outputs', 'Designed for rapid encounter deployment', 'Scalable map generation workflows'], cta: 'Explore Battlemaps', route: '/dashboard/battlemaps', color: '#F59A23', gradient: 'linear-gradient(135deg, #F59A23, #FFB33F)', live: true },
  { key: 'battletracker', icon: '⚔️', title: 'Combat System PRO', desc: 'Run and manage your D&D encounters with a streamlined combat tracking system.', points: ['Encounter tracking', 'Initiative and combat flow', 'Campaign utility', 'Fast, structured control'], cta: 'Open Combat System PRO', route: '/dashboard/battletracker', color: '#19C785', gradient: 'linear-gradient(135deg, #19C785, #35E6A3)', live: true },
];

// ── Stage 7: Recent Build Card with evaluation UI ─────────────────────────────
function RecentBuildCard({ job, formatJobName, formatJobDate, navigate, setActiveVariants }) {
  const [menuOpen, setMenuOpen]         = useState(false);
  const [variantOpen, setVariantOpen]   = useState(false);
  const menuRef                         = useRef(null);

  const cfg        = job.configuration_parameters || {};
  const evaluation = evaluateBuild(cfg);
  const signal     = getBuildSignal(evaluation);
  const messages   = getBuildMessages(evaluation);

  const signalColor = signal === 'green' ? '#16a34a'
    : signal === 'yellow' ? '#d97706'
    : '#dc2626';

  const signalBg = signal === 'green' ? 'rgba(22,163,74,0.12)'
    : signal === 'yellow' ? 'rgba(217,119,6,0.12)'
    : 'rgba(220,38,38,0.12)';

  const signalLabel = signal === 'green' ? 'Build clarity: Strong'
    : signal === 'yellow' ? 'Build clarity: Medium'
    : 'Build clarity: Needs refinement';

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen && !variantOpen) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setVariantOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen, variantOpen]);

  function openEngine(focusHint) {
    try {
      const slug = job.generator_slug;
      let configuration = null;
      try {
        const saved = localStorage.getItem('visinvoke_state_' + slug);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && parsed.state && Object.keys(parsed.state).length > 0) {
            configuration = parsed.state;
          }
        }
      } catch (e) {
        console.warn('VISINVOKE: localStorage read failed', e);
      }
      if (!configuration) {
        if (job.full_state && Object.keys(job.full_state).length > 0) {
          configuration = job.full_state;
        }
      }
      if (!configuration) {
        console.warn('VISINVOKE: Falling back to filtered configuration_parameters');
        configuration = job.configuration_parameters || {};
      }
      const payload = { slug: slug, configuration: configuration };
      sessionStorage.setItem('vi_build', JSON.stringify(payload));
      if (focusHint) {
        const target = GO_TARGET_BY_HINT[focusHint];
        if (target) localStorage.setItem('visinvoke_go_target', JSON.stringify(target));
      }
    } catch(e) {}
    navigate('/engine', { state: { slug: job.generator_slug } });
  }

  function handleVariation(mode) {
    setMenuOpen(false);
    setVariantOpen(false);
    try {
      const slug = job.generator_slug;
      let configuration = null;
      try {
        const saved = localStorage.getItem('visinvoke_state_' + slug);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && parsed.state && Object.keys(parsed.state).length > 0) {
            configuration = parsed.state;
          }
        }
      } catch (e) {
        console.warn('VISINVOKE: localStorage read failed', e);
      }
      if (!configuration) {
        if (job.full_state && Object.keys(job.full_state).length > 0) {
          configuration = job.full_state;
        }
      }
      if (!configuration) {
        console.warn('VISINVOKE: Falling back to filtered configuration_parameters');
        configuration = job.configuration_parameters || {};
      }
      const modifier = VARIATION_MODIFIERS[mode] || '';
      const payload  = { slug: slug, configuration: configuration, _variation_modifier: modifier };
      sessionStorage.setItem('vi_build', JSON.stringify(payload));
    } catch(e) {}
    navigate('/engine', { state: { slug: job.generator_slug } });
  }

  return (
    <div className="vi-build-card" style={rb.card}>
      {/* Indicator bar — top right */}
      <div style={{ ...rb.indicator, ...vgGlass(signalColor, signalColor, signalColor) }} title={signalLabel} data-help-id="build-status">
        <span style={{ ...rb.indicatorDot, background: signalColor }} />
        <span style={{ ...rb.indicatorText, color: '#fff' }}>{signalLabel}</span>
      </div>

      {/* Image */}
      <div style={rb.imgWrap}>
        <img src={job.result.url} alt={formatJobName(job)} className="vi-build-img" style={rb.img} data-help-id="build-image" />
      </div>

      {/* Card body */}
      <div style={rb.body}>
        <div style={rb.name} data-help-id="build-name">{formatJobName(job)}</div>
        <div style={rb.date} data-help-id="build-date">{formatJobDate(job)}</div>

        {/* Micro feedback — only when not strong */}
        {messages.length > 0 && (
          <div style={rb.feedback}>
            {messages.map((m, i) => (
              <div key={i} style={rb.feedbackRow}>
                <span style={rb.feedbackMsg}>· {typeof m === 'string' ? m : m.label}</span>
                {m.layer && (
                  <button
                    style={rb.goBtn}
                    onClick={() => {
                      try {
                        const target = GO_TARGET_BY_LAYER[m.layer];
                        if (target) localStorage.setItem('visinvoke_go_target', JSON.stringify(target));
                      } catch(e) {}
                      openEngine(null);
                    }}
                  >Go</button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Continue + menu row */}
        <div style={rb.actions} ref={menuRef}>
          <button
            data-help-id="build-continue"
            className="vi-dash-continue-recent"
            style={rb.continueBtn}
            onClick={() => openEngine(null)}
          >
            Continue Build
          </button>

          {/* ... menu */}
          <div style={rb.menuWrap}>
            <button
              data-help-id="build-menu"
              style={rb.menuTrigger}
              onClick={() => { setMenuOpen(o => !o); setVariantOpen(false); }}
              title="More options"
            >
              &bull;&bull;&bull;
            </button>
            {menuOpen && (
              <div style={rb.menuDropdown}>
                <button data-help-id="menu-subject" style={rb.menuItem} onClick={() => { setMenuOpen(false); openEngine('subject'); }}>
                  Strengthen Subject Focus
                </button>
                <button data-help-id="menu-environment" style={rb.menuItem} onClick={() => { setMenuOpen(false); openEngine('environment'); }}>
                  Clarify Environment
                </button>
                <button data-help-id="menu-state" style={rb.menuItem} onClick={() => { setMenuOpen(false); openEngine('state'); }}>
                  Enforce State
                </button>
                <div style={rb.menuDivider} />
                <button data-help-id="menu-variation" style={rb.menuItem} onClick={() => { setMenuOpen(false); setVariantOpen(true); }}>
                  Create Variation &#9658;
                </button>
                {(job.versions || []).length > 0 && (
                  <button data-help-id="build-versions" style={rb.menuItem} onClick={() => { setMenuOpen(false); if (setActiveVariants) setActiveVariants(job); }}>
                    View Versions ({(job.versions || []).length})
                  </button>
                )}
              </div>
            )}
            {variantOpen && (
              <div style={rb.menuDropdown}>
                <div style={rb.menuHeader}>Variation Type</div>
                <button data-help-id="var-standard" style={rb.menuItem} onClick={() => handleVariation('standard')}>Standard</button>
                <button data-help-id="var-composition" style={rb.menuItem} onClick={() => handleVariation('composition')}>Stronger Composition</button>
                <button data-help-id="var-cinematic" style={rb.menuItem} onClick={() => handleVariation('cinematic')}>More Cinematic</button>
                <button data-help-id="var-clean" style={rb.menuItem} onClick={() => handleVariation('clean')}>Cleaner Output</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const rb = {
  card: { background: 'rgba(255,255,255,0.06)', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', boxSizing: 'border-box', transition: 'transform 220ms cubic-bezier(0.22,1,0.36,1), box-shadow 220ms cubic-bezier(0.22,1,0.36,1)' },
  indicator: { display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 6, border: '1px solid', margin: '8px 8px 0', alignSelf: 'flex-start' },
  indicatorDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  indicatorText: { fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '0.03em' },
  imgWrap: { height: 220, overflow: 'hidden', flexShrink: 0, margin: '6px 8px 0', borderRadius: 6 },
  img: { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' },
  body: { padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', flex: 1 },
  name: { fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 600, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  date: { fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' },
  feedback: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4, flex: 1 },
  feedbackRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 },
  feedbackMsg: { fontSize: 10, color: 'rgba(255,200,100,0.75)', lineHeight: 1.5, flex: 1 },
  goBtn: { fontSize: 11, padding: '2px 6px', marginLeft: 4, borderRadius: 4, cursor: 'pointer', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', flexShrink: 0 },
  actions: { display: 'flex', gap: 6, alignItems: 'center', marginTop: 'auto', paddingTop: 8, position: 'relative' },
  continueBtn: { ...VG_CAT, flex: 1, borderRadius: 6, padding: '6px 0', fontWeight: 800, fontSize: 11, cursor: 'pointer', transition: 'filter 0.14s, transform 0.12s' },
  menuWrap: { position: 'relative', flexShrink: 0 },
  menuTrigger: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer', letterSpacing: '0.05em', lineHeight: 1 },
  menuDropdown: { position: 'absolute', bottom: '100%', right: 0, marginBottom: 4, background: '#1e1e2e', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 0', minWidth: 180, zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
  menuHeader: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.35)', padding: '4px 12px 2px' },
  menuItem: { display: 'block', width: '100%', background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 500, padding: '7px 12px', cursor: 'pointer', textAlign: 'left', transition: 'background 0.12s' },
  menuDivider: { height: 1, background: 'rgba(255,255,255,0.08)', margin: '4px 0' },
};

function AccountStatusPanel() {
  const { accountState } = useAuth();
  const [history,        setHistory]        = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    API.get('/v1/auth/account-status')
      .then(res => setHistory(res.data?.history || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, []);

  const hasMessage  = accountState?.admin_message;
  const hasHistory  = history.length > 0;
  const actionReq   = accountState?.admin_action_required;
  const status      = accountState?.account_status || 'ACTIVE';
  const restriction = accountState?.restriction_level || 'NONE';

  if (!hasMessage && !hasHistory && !actionReq && status === 'ACTIVE' && restriction === 'NONE') {
    return null;
  }

  const msgType     = accountState?.admin_message_type || 'INFO';
  const borderColor = msgType === 'ERROR' ? '#dc2626' : msgType === 'WARNING' ? '#f97316' : '#2563eb';
  const bgColor     = msgType === 'ERROR' ? 'rgba(220,38,38,0.08)' : msgType === 'WARNING' ? 'rgba(249,115,22,0.08)' : 'rgba(37,99,235,0.08)';
  const dotColor    = status === 'ACTIVE' ? '#10b981' : status === 'PAUSED' ? '#f97316' : status === 'RESTRICTED' ? '#f97316' : status === 'FLAGGED' ? '#f59e0b' : '#dc2626';

  function formatAction(action) {
    const map = {
      WARN: 'Warning Issued', PAUSE: 'Account Paused', RESTRICT: 'Usage Restricted',
      REQUIRE_CONTACT: 'Contact Required', SOFT_BAN: 'Account Suspended',
      FULL_BAN: 'Account Banned', FLAG: 'Account Flagged',
      REMOVE_RESTRICTION: 'Restriction Removed', RESTORE_ACCOUNT: 'Account Restored',
      CLEAR_WARNING: 'Warning Cleared', GRANT_CREDITS: 'Credits Added',
      COMPENSATION: 'Compensation Issued', LOYALTY_BONUS: 'Loyalty Bonus Added',
      COURTESY_CREDIT: 'Courtesy Credit Added',
    };
    return map[action] || action;
  }

  function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  return (
    <div style={ast.panel}>
      <div style={ast.panelHeader}>
        <div style={ast.panelTitle}>Account Status &amp; History</div>
      </div>
      <div style={ast.statusBlock}>
        <div style={ast.statusRow}>
          <span style={{ ...ast.statusDot, background: dotColor }} />
          <span style={ast.statusLabel}>{status.charAt(0) + status.slice(1).toLowerCase()}</span>
          {restriction !== 'NONE' && (
            <span style={ast.restrictionTag}>{restriction.replace('_', ' ')}</span>
          )}
        </div>
        {hasMessage && (
          <div style={{ ...ast.messageBox, borderColor, background: bgColor }}>
            <div style={{ ...ast.messageLabel, color: borderColor }}>Account Notice</div>
            <div style={ast.messageText}>{accountState.admin_message}</div>
          </div>
        )}
      </div>
      {(hasHistory || historyLoading) && (
        <div style={ast.historySection}>
          <div style={ast.historyTitle}>Account History</div>
          {historyLoading ? (
            <div style={ast.historyEmpty}>Loading...</div>
          ) : history.length === 0 ? (
            <div style={ast.historyEmpty}>No account history to display.</div>
          ) : (
            <div style={ast.historyList}>
              {history.map((entry, i) => (
                <div key={entry.id || i} style={ast.historyItem}>
                  <div style={ast.historyItemLeft}>
                    <div style={ast.historyAction}>{formatAction(entry.action)}</div>
                    {entry.public_note && <div style={ast.historyNote}>{entry.public_note}</div>}
                  </div>
                  <div style={ast.historyDate}>{formatDate(entry.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Maps a Quick Link's title to its guided-help target id, so the help engine
// can spotlight each shortcut individually. (Help overlay only; no behaviour change.)
const QL_HELP_IDS = {
  'Explore Battlemaps':      'ql-explore-battlemaps',
  'Open Combat System PRO':  'ql-open-combat-system-pro',
  'Manage Subscription':     'ql-manage-subscription',
  'View Plans':              'ql-view-plans',
  'Browse Generators':       'ql-browse-generators',
  'Browse Professional':     'ql-browse-generators',
  'Build Workspace':         'ql-build-workspace',
};

function BottomSection({ product, isHobbyPack, isTrial, isBattlemaps, jobsLoading, recentJobs, allQuickLinks, navigate, openPasswordModal, formatJobName, formatJobDate, setActiveVariants }) {
  var pal = (product.palette) || CATEGORY_PALETTES[product.key] || CATEGORY_PALETTES.visual;
  return (
    <div style={s.bottomGrid}>
      <div style={s.leftCol}>
        <div style={s.card}>
          <div style={{ ...s.cardLabel, color: product.color }} data-help-id="library-card">{product.dashboardCardTitle}</div>
          <p style={s.cardDesc}>{product.cardDesc}</p>
          {(product.pillsLive.length > 0 || product.pillsSoon.length > 0) && (
            <div style={s.pills}>
              {product.pillsLive.map((p, i) => <span key={i} style={glassPill(pal)} data-help-id={`lib-pill-${i}`}>{p}</span>)}
              {product.pillsSoon.map((p, i) => <span key={i} style={s.pillComingSoon}>{p}</span>)}
            </div>
          )}
          <button style={s.browseBtn} data-help-id="library-browse" onClick={() => navigate(product.browseRoute)}>{product.browseButtonLabel}</button>
        </div>
        <div style={{ ...s.workspaceCard, flex: 1 }} data-help-id="recent-card">
          <div style={s.workspaceCardTop}>
            <div style={s.workspaceCardLeft}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={s.workspaceTitle}>Recent Builds</div>
                <div style={s.workspaceDesc}>Your latest generated images. Continue any build directly from here.</div>
{(() => {
                })()}
              </div>
            </div>
            <div style={s.workspaceBtnGroup}>
              <button style={s.workspaceOpenBtn} data-help-id="recent-workspace" onClick={() => navigate('/workspace')}>Open Workspace</button>
            </div>
          </div>
          {jobsLoading ? <div style={s.recentBuildsEmpty}>Loading...</div> : recentJobs.length === 0 ? <div style={s.recentBuildsEmpty}>No generations yet</div> : (
            <div style={s.recentBuildsGrid}>
              {recentJobs.filter(j => j.status === 'completed' && j.result?.url).slice(0, 3).map(job => (
                <RecentBuildCard
                  key={job.job_id}
                  job={job}
                  formatJobName={formatJobName}
                  formatJobDate={formatJobDate}
                  navigate={navigate}
                  setActiveVariants={setActiveVariants}
                />
              ))}
              {recentJobs.filter(j => j.status === 'completed' && j.result?.url).length === 0 && <div style={s.recentBuildsEmpty}>No completed builds yet</div>}
            </div>
          )}
        </div>
      </div>
      <div style={{ ...s.card, boxSizing: 'border-box' }}>
        <div style={{ ...s.cardLabel, color: product.color }} data-help-id="quicklinks-card">Quick Links</div>
        <div style={s.quickLinks}>
          {allQuickLinks.map((link, i) => {
            if (link.hobbyPackHide && isHobbyPack) return null;
            return (
              <button key={i} data-help-id={QL_HELP_IDS[link.title]} className="vi-quicklink-hover" style={s.quickLink} onClick={() => {
                if (link.route === '/upgrade#hobby') { navigate('/upgrade'); setTimeout(() => { const el = document.getElementById('hobby'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 300); return; }
                if (link.isPasswordChange) { openPasswordModal(); return; }
                if (link.route.startsWith('http')) { window.location.href = link.route; return; }
                navigate(link.route);
              }}>
                <span style={s.quickIcon}>{link.icon}</span>
                <div>
                  <div style={s.quickTitle}>{link.title}</div>
                  <div style={s.quickSub}>{link.sub}</div>
                </div>
              </button>
            );
          })}
          {!isBattlemaps && (
            <button style={s.quickLink} data-help-id="ql-view-recent-builds" onClick={() => navigate('/workspace')}>
              <span style={s.quickIcon}>&#x1F5C2;</span>
              <div><div style={s.quickTitle}>View Recent Builds</div><div style={s.quickSub}>Continue your latest generator builds</div></div>
            </button>
          )}
          {isBattlemaps && (
            <>
              <button style={s.quickLink} onClick={() => navigate('/workspace')}>
                <span style={s.quickIcon}>&#x1F5C2;</span>
                <div><div style={s.quickTitle}>View Recent Maps</div><div style={s.quickSub}>Continue your latest battlemaps</div></div>
              </button>
              <button style={s.quickLink} onClick={() => navigate('/workspace')}>
                <span style={s.quickIcon}>&#x1F6E0;&#xFE0F;</span>
                <div><div style={s.quickTitle}>My Builds</div><div style={s.quickSub}>Access your saved battlemaps</div></div>
              </button>
              <button style={s.quickLink} onClick={() => navigate('/generators?library=battlemaps')}>
                <span style={s.quickIcon}>&#x1F5FA;&#xFE0F;</span>
                <div><div style={s.quickTitle}>Browse Map Packs</div><div style={s.quickSub}>Explore available battlemaps</div></div>
              </button>
              <button style={s.quickLink} onClick={() => navigate('/workspace')}>
                <span style={s.quickIcon}>&#x1F4CB;</span>
                <div><div style={s.quickTitle}>Go to Workspace</div><div style={s.quickSub}>Manage builds, presets, and history</div></div>
              </button>
            </>
          )}
          <button style={s.quickLink} data-help-id="ql-rewards-referrals" onClick={() => navigate('/rewards')}>
            <span style={s.quickIcon}>&#x1F381;</span>
            <div><div style={s.quickTitle}>Rewards &amp; Referrals</div><div style={s.quickSub}>Earn credits by sharing VISINVOKE</div></div>
          </button>
          <button style={s.quickLink} data-help-id="ql-change-password" onClick={openPasswordModal}>
            <span style={s.quickIcon}>&#x1F512;</span>
            <div><div style={s.quickTitle}>Change Password</div><div style={s.quickSub}>Update your account password</div></div>
          </button>
        </div>
      </div>
    </div>
  );
}

function BattleTrackerGate({ user, navigate }) {
  // Entitlement state -- seeded from user prop, refreshable without page reload
  const [combatPro,  setCombatPro]   = useState(user?.entitlements?.combat_system_pro === true);
  const [purchasing, setPurchasing]  = useState(false);
  const [purchaseErr, setPurchaseErr] = useState('');
  const [tier, setTier] = useState('none');
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();

  // Re-fetch permanent ownership and the server-authoritative Cloud plan on mount.
  useEffect(() => {
    API.get('/v1/auth/me').then(res => {
      const ents = res.data?.user?.entitlements || {};
      setCombatPro(!!ents.combat_system_pro);
    }).catch(() => {});

    API.get('/v1/combat-commerce/account').then((res) => {
      const cloud = res.data?.combat_cloud;
      setTier(cloud?.status === 'active' ? cloud.tier : 'none');
    }).catch(() => setTier('none'));
    const off = () => {};

    const search = window.location.search;
    if (search.includes('purchase=combat_pro_success')) {
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        API.get('/v1/auth/me').then(res => {
          const ents = res.data?.user?.entitlements || {};
          if (ents.combat_system_pro) {
            setCombatPro(true);
            clearInterval(poll);
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }).catch(() => {});
        if (attempts >= 8) clearInterval(poll);
      }, 1000);
      return () => { clearInterval(poll); off(); };
    }
    return off;
  }, []);

  async function handleUnlockNow() {
    setPurchasing(true);
    setPurchaseErr('');
    try {
      const res = await consentCheckout.start(() => API.post('/api/payments/create-combat-pro', {}), { purchaseType: 'combat_system_pro', intendedSelector: null });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setPurchaseErr('Could not start checkout. Please try again.');
      }
    } catch (e) {
      setPurchaseErr(e.response?.data?.error || 'Could not start checkout. Please try again.');
    } finally {
      setPurchasing(false);
    }
  }

  function handleLaunch() {
    if (!owned) return;
    navigate('/battletracker');
  }

  const owned = getCombatProOwned(combatPro);
  const tierDef = COMBAT_TIERS[tier] || COMBAT_TIERS.none;
  const tierPriceLabel = tierDef.price ? ('$' + tierDef.price.toFixed(2) + tierDef.per) : 'Free';
  const tierSpec = getTierSpec(tier);

  const COMBAT_PRO_MEMBERSHIP_INFO = {
    none:  { savedEncounters: '2',  cloudSaves: 'No',  monsterCloud: 'No',  spellCloud: 'No',  encounterCloud: 'No', customSync: 'No',  benefits: 'Local only', currentTier: 'No Cloud', upgradeAvailable: 'Cloud 1 / 2 / 3' },
    tier1: { savedEncounters: '6',  cloudSaves: 'Yes', monsterCloud: 'Yes', spellCloud: 'Yes', encounterCloud: 'Yes', customSync: 'Yes', benefits: 'Cloud & sync', currentTier: 'Cloud 1', upgradeAvailable: 'Cloud 2 / 3' },
    tier2: { savedEncounters: '8',  cloudSaves: 'Yes', monsterCloud: 'Yes', spellCloud: 'Yes', encounterCloud: 'Yes', customSync: 'Yes', benefits: 'Cloud & sync', currentTier: 'Cloud 2', upgradeAvailable: 'Cloud 3' },
    tier3: { savedEncounters: '12', cloudSaves: 'Yes', monsterCloud: 'Yes', spellCloud: 'Yes', encounterCloud: 'Yes', customSync: 'Yes', benefits: 'Cloud & sync', currentTier: 'Cloud 3', upgradeAvailable: '\u2014' },
  };
  const cpInfo = COMBAT_PRO_MEMBERSHIP_INFO[tier] || COMBAT_PRO_MEMBERSHIP_INFO.none;
  const cpYes = (v) => v === 'Yes' || v === 'Included';
  const cpValColor = (v) => (v === 'No' || v === 'None' || v === 'Locked') ? 'rgba(255,255,255,0.6)' : (cpYes(v) ? '#4ade80' : '#fff');

  return (
    <>
      <GuardedCheckoutConsent checkout={consentCheckout} />
      {owned && (
        <button style={btg.launchBar} onClick={handleLaunch}>🚀 Launch Combat PRO</button>
      )}
      {/* ── TOP AREA — OWNERSHIP + MEMBERSHIP (50/50) ── */}
      <div style={btg.twoColGrid}>

        {/* LEFT — Combat PRO ownership */}
        <div style={btg.bigCard}>
          <div style={btg.bcKicker}>Combat PRO Ownership</div>
          <div style={btg.bcTitleRow}>
            <div>
              <div style={btg.bcTitle}>Combat PRO</div>
              <div style={btg.bcPrice}>$49.99 <span style={btg.bcPriceUnit}>USD</span></div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>One-Time Purchase</div>
            </div>
            <span style={{ ...btg.bigBadge, ...(owned ? { background: 'rgba(74,222,128,0.18)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.5)' } : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.18)' }) }}>
              {owned ? 'Owned ✓' : 'Not Owned'}
            </span>
          </div>
          <div style={btg.ftGrid}>
            {[
              ['469 Monsters', 'Ready-to-use DND monster library for fast encounter setup, live combat reference and creature selection.'],
              ['319 Spells', 'Searchable spell library for combat reference, creature abilities, spell checks and faster rulings.'],
              ['Custom Monsters', 'Create, edit and manage custom monsters so Combat PRO can match your campaign, homebrew content and table style.'],
              ['Custom Spells', 'Add and customise spells for your campaign, creatures, homebrew rules and table-specific spell use.'],
              ['Live Data Editing', 'Edit encounter, monster, spell and combat data on the fly during play without stopping the session.'],
              ['DM Difficulty Control', 'Adjust combat during the session to make encounters easier, harder or better balanced as the table needs.'],
              ['Encounter Monster Capacity', 'Build live encounters with up to 16 monsters active at one time.'],
              ['Dedicated Encounters', 'Set up, save and control up to 12 dedicated DND encounters for organised live gameplay.'],
              ['Live Gameplay Use', 'Use Combat PRO during active DND sessions after the one-time purchase, without needing a monthly tier for core play.'],
              ['Initiative Tracking', 'Track turn order, rounds, players and monsters clearly during combat.'],
              ['Combat Conditions', 'Apply and manage DND conditions, durations and combat effects during play.'],
              ['Templates', 'Reuse encounter structures for faster preparation and repeated setups.'],
              ['Combat Logs', 'Track important combat actions, events and decisions during the encounter.'],
              ['Legendary and Recharge Tracking', 'Manage legendary creature actions and recharge abilities clearly inside the combat flow.'],
              ['Dice Engine', 'Roll and resolve combat dice directly inside the encounter workspace.'],
              ['Cloud Progress Upgrade', 'Tier 1 cloud membership adds saved progress, cloud continuity and account-based access for ongoing Combat PRO use.'],
            ].map((row, i) => {
              const isUpgrade = row[0] === 'Cloud Progress Upgrade';
              return (
                <div key={i} style={isUpgrade ? { ...btg.ftItem, ...btg.ftItemUpgrade } : btg.ftItem}>
                  <div style={btg.ftName}>
                    {isUpgrade
                      ? <span style={{ ...btg.ftTick, color: '#35E6A3' }}>&#8593;</span>
                      : <span style={btg.ftTick}>&#10003;</span>}
                    <span>{row[0]}</span>
                  </div>
                  <div style={btg.ftSub}>{row[1]}</div>
                </div>
              );
            })}
          </div>
          {!owned && (
            <>
              <button style={{ ...btg.bcBtn, ...btg.bcBtnLocked, opacity: purchasing ? 0.6 : 1 }} onClick={handleUnlockNow} disabled={purchasing}>
                {purchasing ? 'Starting...' : 'Purchase Combat PRO'}
              </button>
              {purchaseErr && <div style={btg.stripErr}>{purchaseErr}</div>}
            </>
          )}
        </div>

        {/* RIGHT — Current Membership */}
        <div style={btg.bigCard}>
          <div style={btg.bcKicker}>Current Membership</div>
          <div style={btg.bcTitleRow}>
            <span style={{ ...btg.bigBadge, background: tierDef.color + '26', color: tierDef.color, border: '1px solid ' + tierDef.color + '99' }}>{tierDef.name}</span>
            <span style={btg.bcRowVal}>{tierPriceLabel}</span>
          </div>
          <div style={btg.heroTiles}>
            <div style={btg.heroTile}>
              <div style={btg.heroTileNum}>{tierSpec.savedEncounters}</div>
              <div style={btg.heroTileLbl}>Saved Encounters</div>
            </div>
            <div style={{ ...btg.heroTile, ...btg.heroTileGen }}>
              <div style={btg.heroTileNumGen}>{tier === 'none' ? 'Local' : 'Active'}</div>
              <div style={btg.heroTileLbl}>Cloud Sync</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, flex: 1 }}>
            <div style={btg.bcRows}>
              <div style={btg.bcRow}><span style={btg.bcRowLabel}>Saved Encounters</span><span style={{ ...btg.bcRowVal, color: cpValColor(cpInfo.savedEncounters) }}>{cpInfo.savedEncounters}</span></div>
              <div style={btg.bcRow}><span style={btg.bcRowLabel}>Cloud Saves</span><span style={{ ...btg.bcRowVal, color: cpValColor(cpInfo.cloudSaves) }}>{cpInfo.cloudSaves}</span></div>
              <div style={btg.bcRow}><span style={btg.bcRowLabel}>Monster Cloud</span><span style={{ ...btg.bcRowVal, color: cpValColor(cpInfo.monsterCloud) }}>{cpInfo.monsterCloud}</span></div>
              <div style={btg.bcRow}><span style={btg.bcRowLabel}>Spell Cloud</span><span style={{ ...btg.bcRowVal, color: cpValColor(cpInfo.spellCloud) }}>{cpInfo.spellCloud}</span></div>
              <div style={btg.bcRow}><span style={btg.bcRowLabel}>Encounter Cloud</span><span style={{ ...btg.bcRowVal, color: cpValColor(cpInfo.encounterCloud) }}>{cpInfo.encounterCloud}</span></div>
            </div>
            <div style={btg.bcRows}>
              <div style={btg.bcRow}><span style={btg.bcRowLabel}>Custom Content Sync</span><span style={{ ...btg.bcRowVal, color: cpValColor(cpInfo.customSync) }}>{cpInfo.customSync}</span></div>
              <div style={btg.bcRow}><span style={btg.bcRowLabel}>Membership Benefits</span><span style={{ ...btg.bcRowVal, color: cpValColor(cpInfo.benefits) }}>{cpInfo.benefits}</span></div>
              <div style={btg.bcRow}><span style={btg.bcRowLabel}>Current Tier</span><span style={{ ...btg.bcRowVal, color: cpValColor(cpInfo.currentTier) }}>{cpInfo.currentTier}</span></div>
              <div style={btg.bcRow}><span style={btg.bcRowLabel}>Upgrade Available</span><span style={{ ...btg.bcRowVal, color: cpValColor(cpInfo.upgradeAvailable) }}>{cpInfo.upgradeAvailable}</span></div>
            </div>
          </div>
          <div style={btg.actionsWrap}>
            <div style={btg.bcKicker}>Account &amp; Product Actions</div>
            <div style={btg.actionsGrid}>
              <button style={btg.actionBtn} onClick={() => viOpenCombatPricing(navigate)}>🏷️ Pricing</button>
              <button style={btg.actionBtn} onClick={() => viOpenCombatMembership(navigate)}>⭐ Membership</button>
              <button style={btg.actionBtn} onClick={() => viOpenCombatPurchaseHistory(navigate)}>🧾 Purchase History</button>
            </div>
          </div>
        </div>

      </div>

      {/* ── TIP BAR ── */}
      <div style={btg.tipBar}>
        <div style={btg.tipLeft}>
          <span style={btg.tipIconCircle}>i</span>
          <span style={btg.tipText}><strong style={{ color: '#fff', fontWeight: 800 }}>Tip:</strong> Choose a Cloud plan for sync and prepared encounter capacity. Standalone generators are subscribed separately.</span>
        </div>
        <button style={btg.tipBtn} onClick={() => viOpenCombatMembership(navigate)}>View Membership Options</button>
      </div>

      {/* ── COMBAT PRO COMMAND CENTRE ── */}
      {(() => {
        const CC_DASH = '\u2014';
        const cardStyle = { background: 'rgba(13, 42, 32,0.9)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 16, padding: '22px 24px', boxSizing: 'border-box' };
        const headStyle = { fontSize: 13, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 14 };
        const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' };
        const lblStyle = { fontSize: 13, color: 'rgba(255,255,255,0.62)', fontWeight: 600 };
        const valStyle = { fontSize: 14, color: '#fff', fontWeight: 700 };
        const tileStyle = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '16px 12px', textAlign: 'center' };
        const tileNum = { fontSize: 22, fontWeight: 800, color: '#fff' };
        const tileLbl = { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginTop: 6 };
        const qaBtn = { background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 9, padding: '12px 14px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', textAlign: 'left' };
        const recentActivity = [
          'Last Encounter Opened', 'Last Generator Used', 'Last Monster Created',
          'Last Template Used', 'Last Snapshot',
        ];
        const recentSaves = ['Recent Encounter', 'Recent Template', 'Recent Monster', 'Recent Snapshot'];
        const systemSummary = [
          'Official Monsters', 'Official Spells', 'Custom Monsters',
          'Templates', 'Snapshots', 'Saved Encounters',
        ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>

            {/* Recent Activity */}
            <div style={cardStyle}>
              <div style={headStyle}>Recent Activity</div>
              {recentActivity.map((label, i) => (
                <div key={i} style={rowStyle}>
                  <span style={lblStyle}>{label}</span>
                  <span style={valStyle}>{CC_DASH}</span>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div style={cardStyle}>
              <div style={headStyle}>Quick Actions</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button style={qaBtn} onClick={handleLaunch}>Continue Last Encounter</button>
                <button style={qaBtn} onClick={handleLaunch}>Open Monster Studio</button>
                <button style={qaBtn} onClick={handleLaunch}>Open Encounter Templates</button>
                <button style={qaBtn} onClick={() => viOpenCombatMembership(navigate)}>Open Membership</button>
                <button style={qaBtn} onClick={() => viOpenCombatPricing(navigate)}>Open Pricing</button>
              </div>
            </div>

            {/* Recent Saves */}
            <div style={cardStyle}>
              <div style={headStyle}>Recent Saves</div>
              {recentSaves.map((label, i) => (
                <div key={i} style={rowStyle}>
                  <span style={lblStyle}>{label}</span>
                  <span style={valStyle}>{CC_DASH}</span>
                </div>
              ))}
            </div>

            {/* System Summary */}
            <div style={cardStyle}>
              <div style={headStyle}>System Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {systemSummary.map((label, i) => (
                  <div key={i} style={tileStyle}>
                    <div style={tileNum}>{CC_DASH}</div>
                    <div style={tileLbl}>{label}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        );
      })()}

      {/* ── FEATURE PANEL — Everything You Need for Epic Encounters ── */}
      <style>{`
        .cpfp-grid { display: grid; grid-template-columns: repeat(6, 1fr); column-gap: 0; row-gap: 24px; }
        .cpfp-col { padding: 0 22px; border-left: 1px solid rgba(255,255,255,0.1); }
        .cpfp-col:first-child { border-left: none; }
        @media (max-width: 1499px) {
          .cpfp-grid { grid-template-columns: repeat(3, 1fr); row-gap: 24px; }
          .cpfp-col { border-left: 1px solid rgba(255,255,255,0.1); }
          .cpfp-col:nth-child(3n+1) { border-left: none; }
        }
        @media (max-width: 999px) {
          .cpfp-grid { grid-template-columns: repeat(2, 1fr); row-gap: 24px; }
          .cpfp-col { border-left: none; }
        }
        @media (max-width: 699px) {
          .cpfp-grid { grid-template-columns: repeat(1, 1fr); row-gap: 22px; }
          .cpfp-col { border-left: none; padding: 0 10px; }
        }
      `}</style>

      <div style={btg.featPanel}>
        <div style={btg.featHeading}>Everything You Need for Epic Encounters</div>
        <div style={btg.featSubtitle}>Powerful features built for Dungeon Masters who want more control, more tools, and more unforgettable sessions.</div>

        <div className="cpfp-grid">

          <div className="cpfp-col" style={btg.featCol}>
            <div style={{ ...btg.featIconRing, borderColor: '#19C785', background: 'rgba(25,199,133,0.12)' }}>⚔️</div>
            <div style={btg.featColTitle}>Complete Combat Management</div>
            <div style={btg.featColDesc}>Run full D&amp;D encounters with up to 16 monsters. Track every action, state, and outcome in real time.</div>
            <ul style={btg.featBullets}>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Initiative &amp; turn tracking</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Multi-monster scaling</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Automatic history logging</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Real-time updates</li>
            </ul>
          </div>

          <div className="cpfp-col" style={btg.featCol}>
            <div style={{ ...btg.featIconRing, borderColor: '#22c55e', background: 'rgba(34,197,94,0.12)' }}>🐉</div>
            <div style={btg.featColTitle}>469 Monsters Ready Instantly</div>
            <div style={btg.featColDesc}>Search and load complete stat blocks in seconds. No setup, no manual entry, everything pre-built.</div>
            <ul style={btg.featBullets}>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Full monster database</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Weapons, abilities, spells</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Legendary &amp; lair actions</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Resistances &amp; immunities</li>
            </ul>
          </div>

          <div className="cpfp-col" style={btg.featCol}>
            <div style={{ ...btg.featIconRing, borderColor: '#f97316', background: 'rgba(249,115,22,0.12)' }}>🔥</div>
            <div style={btg.featColTitle}>319 Spells with Execution Engine</div>
            <div style={btg.featColDesc}>Cast spells with real combat logic built in. Handles rolls, saves, AoE and conditions automatically.</div>
            <ul style={btg.featBullets}>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Damage &amp; saving throw</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>AoE targeting &amp; conditions</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Duration &amp; concentration</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Integrated into flow</li>
            </ul>
          </div>

          <div className="cpfp-col" style={btg.featCol}>
            <div style={{ ...btg.featIconRing, borderColor: '#3b82f6', background: 'rgba(59,130,246,0.12)' }}>🧠</div>
            <div style={btg.featColTitle}>Custom Content Creation</div>
            <div style={btg.featColDesc}>Create monsters and spells from scratch. Fully integrated into Combat PRO.</div>
            <ul style={btg.featBullets}>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Monster builder</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Spell creation system</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Persistent storage</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Instant availability</li>
            </ul>
          </div>

          <div className="cpfp-col" style={btg.featCol}>
            <div style={{ ...btg.featIconRing, borderColor: '#14b8a6', background: 'rgba(20,184,166,0.12)' }}>🎯</div>
            <div style={btg.featColTitle}>Combat Intelligence &amp; Tracking</div>
            <div style={btg.featColDesc}>Automate complex combat mechanics easily. Tracks effects, morale, and conditions.</div>
            <ul style={btg.featBullets}>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>20+ conditions</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Morale system</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Effect tracking</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Logic automation</li>
            </ul>
          </div>

          <div className="cpfp-col" style={btg.featCol}>
            <div style={{ ...btg.featIconRing, borderColor: '#eab308', background: 'rgba(234,179,8,0.12)' }}>🎲</div>
            <div style={btg.featColTitle}>Real-Time System Experience</div>
            <div style={btg.featColDesc}>Designed for speed and clarity in combat. Fast actions, clear feedback, no confusion.</div>
            <ul style={btg.featBullets}>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Physics-based dice rolling</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Quick roll + custom combos</li>
              <li style={btg.featBulletItem}><span style={btg.featBulletDot}>&bull;</span>Full roll history tracking</li>
            </ul>
          </div>

        </div>
      </div>
    </>
  );
}

const btg = {
  heroTiles:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '2px 0 4px' },
  heroTile:      { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: '20px 10px', textAlign: 'center' },
  heroTileGen:   { background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.45)' },
  heroTileNum:   { fontSize: 30, fontWeight: 800, color: '#fff', lineHeight: 1 },
  heroTileNumGen:{ fontSize: 30, fontWeight: 800, color: '#fbbf24', lineHeight: 1 },
  heroTileLbl:   { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginTop: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  navRow:        { display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 24 },
  navBtn:        { minWidth: 190, background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 8, padding: '12px 18px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', textAlign: 'center' },
  actionsWrap:   { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 },
  actionsGrid:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  actionBtn:     { width: '100%', background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 8, padding: '16px 14px', fontWeight: 700, fontSize: 14, cursor: 'pointer', textAlign: 'center' },
  twoColGrid:    { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, width: '100%', marginBottom: 0, alignItems: 'stretch' },
  bigCard:       { background: 'rgba(13, 42, 32,0.9)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 16, padding: '26px', display: 'flex', flexDirection: 'column', gap: 14, height: '100%', boxSizing: 'border-box' },
  bcKicker:      { fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' },
  bcTitleRow:    { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  bcTitle:       { fontSize: 22, fontWeight: 800, color: '#fff' },
  bcPrice:       { fontSize: 30, fontWeight: 800, color: '#fff', lineHeight: 1.1, marginTop: 4 },
  bcPriceUnit:   { fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.55)' },
  bigBadge:      { padding: '7px 18px', borderRadius: 999, fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap' },
  bcList:        { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 },
  bcListItem:    { fontSize: 13.5, color: 'rgba(255,255,255,0.85)', display: 'flex', gap: 8, lineHeight: 1.4 },
  ftGrid:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '2px 0 2px' },
  ftItem:        { display: 'flex', flexDirection: 'column', gap: 4, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px' },
  ftItemUpgrade: { background: 'rgba(25,199,133,0.12)', border: '1px solid rgba(25,199,133,0.42)' },
  ftName:        { fontSize: 14, fontWeight: 800, color: '#fff', display: 'flex', gap: 7, alignItems: 'baseline', lineHeight: 1.25 },
  ftTick:        { color: '#4ade80', fontWeight: 800, fontSize: 12, flexShrink: 0 },
  ftSub:         { fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.62)', lineHeight: 1.45 },
  bcRows:        { display: 'flex', flexDirection: 'column', gap: 0, flex: 1, justifyContent: 'space-between' },
  bcRow:         { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  bcRowLabel:    { fontSize: 13, color: 'rgba(255,255,255,0.62)', fontWeight: 600 },
  bcRowVal:      { fontSize: 14, color: '#fff', fontWeight: 700 },
  bcBtn:         { marginTop: 'auto', border: 'none', borderRadius: 9, padding: '13px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer', width: '100%', textAlign: 'center' },
  bcBtnLaunch:   { ...glassSurface('#087C56','#19C785','#35E6A3') },
  launchBar:     { ...glassSurface('#087C56','#19C785','#35E6A3'), width: '100%', boxSizing: 'border-box', borderRadius: 12, padding: '16px 20px', fontSize: 16, fontWeight: 800, cursor: 'pointer', textAlign: 'center', marginBottom: 16 },
  bcBtnLocked:   { ...glassSurface('#9A1F2C','#EF445A','#FF7E90') },
  bcBtnManage:   { ...glassSurface('#334155','#64748B','#94A3B8') },
  // ── Status strip ──
  statusStrip:        { width: '100%', padding: '14px 20px', marginBottom: 24, borderRadius: 10, background: 'linear-gradient(135deg, rgba(13, 42, 32,0.92) 0%, rgba(18, 56, 42,0.88) 100%)', border: '1px solid rgba(255,255,255,0.1)', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  statusBadgeOn:      { color: '#4ade80', fontSize: 14, fontWeight: 700 },
  statusBadgeOff:     { color: 'rgba(255,255,255,0.4)', fontSize: 14, fontWeight: 500 },
  statusDivider:      { color: 'rgba(255,255,255,0.2)', fontSize: 14 },
  stripUnlockBtn:     { marginLeft: 'auto', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  tierBadgePill:      { padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 800 },
  btnRow:             { display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 },
  actBtn:             { background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 8, padding: '11px 18px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' },
  actBtnPrimary:      { background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none' },
  actBtnDisabled:     { opacity: 0.45, cursor: 'not-allowed' },

  // ── Grids ──
  topGrid:            { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, width: '100%', marginBottom: 20 },
  midGrid:            { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, width: '100%', marginBottom: 20 },
  bottomGrid:         { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, width: '100%' },

  // ── Universal card (dark) ──
  uniCard:            { background: 'rgba(13, 42, 32,0.88)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 14 },
  uniCardDark:        { background: 'rgba(13, 42, 32,0.88)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 14, position: 'relative' },
  uniCardLight:       { background: 'rgba(255,255,255,0.97)', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 16, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.09)', position: 'relative' },

  // ── Card internals (dark) ──
  uniIcon:            { fontSize: 36, lineHeight: 1 },
  uniTitle:           { fontSize: 18, fontWeight: 800, color: '#fff', margin: 0, lineHeight: 1.25 },
  uniSubtext:         { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 },
  uniBody:            { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 14, color: 'rgba(255,255,255,0.78)', lineHeight: 1.6 },
  uniBullets:         { margin: '4px 0 0 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 7, flex: 1 },
  uniBulletItem:      { fontSize: 13, color: 'rgba(255,255,255,0.82)', fontWeight: 500, lineHeight: 1.4 },
  uniFooterEmpty:     { height: 42, flexShrink: 0 },
  uniFooter:          { marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  uniOwnedBadge:      { color: '#4ade80', fontSize: 13, fontWeight: 700 },
  uniPriceLabel:      { color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 500 },
  uniErr:             { color: '#fca5a5', fontSize: 12, textAlign: 'center' },
  uniPrimaryBtn:      { background: '#fff', color: '#1a1a1a', border: 'none', borderRadius: 9, padding: '13px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer', width: '100%', textAlign: 'center', boxShadow: '0 3px 10px rgba(0,0,0,0.18)' },
  uniUnlockBtn:       { background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: '#fff', border: 'none', borderRadius: 9, padding: '13px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer', width: '100%', textAlign: 'center', boxShadow: '0 3px 10px rgba(220,38,38,0.35)' },

  // ── Card internals (light) ──
  uniTitleDark:       { fontSize: 18, fontWeight: 800, color: '#1a1a1a', margin: 0, lineHeight: 1.25 },
  uniSubtextDark:     { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13, color: '#6b7280', lineHeight: 1.55 },
  uniBodyDark:        { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 14, color: '#374151', lineHeight: 1.6 },
  uniBulletsDark:     { margin: '4px 0 0 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 7, flex: 1 },
  uniBulletItemDark:  { fontSize: 13, color: '#374151', fontWeight: 500, lineHeight: 1.4 },
  uniFooterLight:     { marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  uniPriceLabelDark:  { color: '#6b7280', fontSize: 12, fontWeight: 500 },

  // ── Strip styles (middle row) ──
  strip:              { display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 16, padding: '14px 18px', minHeight: 90, maxHeight: 110, overflow: 'hidden', borderRadius: 12, boxSizing: 'border-box' },
  stripOwned:         { background: 'rgba(13, 42, 32,0.88)', border: '1px solid rgba(74,222,128,0.25)' },
  stripLocked:        { background: 'rgba(13, 42, 32,0.88)', border: '1px solid rgba(255,255,255,0.1)' },
  stripDisabled:      { background: 'rgba(13, 42, 32,0.5)', border: '1px solid rgba(255,255,255,0.06)', opacity: 0.6, pointerEvents: 'none' },
  stripIcon:          { fontSize: 28, lineHeight: 1, flexShrink: 0 },
  stripInfo:          { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  stripTitle:         { fontSize: 15, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' },
  stripStatOn:        { fontSize: 13, color: '#4ade80', fontWeight: 600, whiteSpace: 'nowrap' },
  stripStatOff:       { fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 500, whiteSpace: 'nowrap' },
  stripBtnPrimary:    { background: 'linear-gradient(135deg, #2f7cff, #4da3ff)', color: '#ffffff', border: 'none', borderRadius: 10, padding: '14px 28px', fontWeight: 600, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1, boxShadow: '0 6px 18px rgba(47,124,255,0.35)', transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease', flexShrink: 0 },
  stripBtnUnlock:     { background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 800, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(220,38,38,0.3)', flexShrink: 0 },
  stripBtnSub:        { background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 800, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(37,99,235,0.3)', flexShrink: 0 },
  stripBtnManage:     { background: 'transparent', color: '#7fb3ff', border: '1px solid rgba(127,179,255,0.5)', borderRadius: 10, padding: '12px 22px', fontWeight: 500, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease' },
  stripErr:           { fontSize: 11, color: '#fca5a5', whiteSpace: 'nowrap' },

  // ── Preserved logic styles ──
  priceCardLocked:    { opacity: 0.6, pointerEvents: 'none', cursor: 'not-allowed', userSelect: 'none' },
  priceBadge:         { position: 'absolute', top: 16, right: 16, background: '#eff6ff', color: '#1d4ed8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '3px 10px', borderRadius: 20 },
  subscribeBtn:       { background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', border: 'none', borderRadius: 9, padding: '13px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer', width: '100%', textAlign: 'center', boxShadow: '0 3px 10px rgba(37,99,235,0.3)' },
  subscribedBadge:    { background: '#eff6ff', color: '#1d4ed8', borderRadius: 9, padding: '12px 0', fontWeight: 700, fontSize: 14, textAlign: 'center', width: '100%' },
  manageSubBtn:       { background: 'transparent', color: '#2563eb', border: '1.5px solid #2563eb', borderRadius: 9, padding: '10px 0', fontWeight: 600, fontSize: 13, cursor: 'pointer', width: '100%', textAlign: 'center' },
  lockOverlay:        { position: 'absolute', inset: 0, borderRadius: 16, background: 'rgba(10,8,30,0.82)', backdropFilter: 'blur(6px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, zIndex: 10, padding: 32 },
  lockIcon:           { fontSize: 36, lineHeight: 1 },
  lockTitle:          { color: '#fff', fontWeight: 800, fontSize: 17, textAlign: 'center' },
  lockSub:            { color: 'rgba(255,255,255,0.65)', fontSize: 13, textAlign: 'center' },
  unlockBtn:          { background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: '#fff', border: 'none', borderRadius: 9, padding: '12px 26px', fontWeight: 800, fontSize: 14, cursor: 'pointer', marginTop: 8, boxShadow: '0 4px 14px rgba(220,38,38,0.4)' },
  cloudStatusPastDue: { width: '100%', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 9, padding: '11px 14px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#9a3412' },
  cloudStatusSuspended: { width: '100%', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 9, padding: '11px 14px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#991b1b' },
  cloudPeriodEnd:     { fontSize: 12, color: '#6b7280', textAlign: 'center' },

  // ── Feature panel (Everything You Need for Epic Encounters) ──
  featPanel:          { width: '100%', background: 'linear-gradient(180deg, rgba(13, 42, 32,0.92) 0%, rgba(40,26,86,0.92) 100%)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '40px 34px 44px', boxSizing: 'border-box', marginBottom: 18 },
  featHeading:        { fontSize: 26, fontWeight: 800, color: '#fff', textAlign: 'center', margin: 0, lineHeight: 1.2 },
  featSubtitle:       { fontSize: 14, color: 'rgba(255,255,255,0.6)', textAlign: 'center', maxWidth: 760, margin: '10px auto 36px', lineHeight: 1.55 },
  featCol:            { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },
  featIconRing:       { width: 72, height: 72, borderRadius: '50%', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, lineHeight: 1, marginBottom: 16, flexShrink: 0 },
  featColTitle:       { fontSize: 16, fontWeight: 800, color: '#fff', lineHeight: 1.3, marginBottom: 12, minHeight: 44, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  featColDesc:        { fontSize: 12.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55, marginBottom: 16, minHeight: 58 },
  featBullets:        { listStyle: 'none', margin: '0 auto', padding: 0, display: 'flex', flexDirection: 'column', gap: 9, width: 'fit-content', maxWidth: '100%', textAlign: 'left', minHeight: 96 },
  featBulletItem:     { fontSize: 13.5, color: 'rgba(255,255,255,0.82)', lineHeight: 1.4, display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left' },
  featBulletDot:      { color: 'rgba(255,255,255,0.45)', flexShrink: 0, lineHeight: 1.4 },

  // ── Tip bar ──
  tipBar:             { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', background: 'rgba(13, 42, 32,0.85)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '16px 22px', boxSizing: 'border-box', margin: '28px 0 24px' },
  tipLeft:            { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: '1 1 auto' },
  tipIconCircle:      { width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg, #19C785, #087C56)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, fontStyle: 'italic', flexShrink: 0 },
  tipText:            { fontSize: 13.5, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 },
  tipBtn:             { background: 'linear-gradient(135deg, #19C785, #087C56)', color: '#fff', border: 'none', borderRadius: 9, padding: '11px 22px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 3px 12px rgba(25,199,133,0.35)', flexShrink: 0 },
};


export default function Dashboard() {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const { user } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const product = resolveProduct(location.pathname);

  useEffect(() => {
    if (window.location.pathname === '/engine' || window.location.search) {
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, []);

  useEffect(() => { localStorage.setItem(LAST_PRODUCT_KEY, product.key); }, [product.key]);

  // Universal product activity for the ONE dashboard product whose usage no domain table can
  // prove. D&D, Professional and Battlemaps usage is already recorded by generator runs and
  // generation jobs, and emitting here too would create a second counter that disagrees with the
  // first. Combat PRO has no server-side run record - its encounters are client state - so opening
  // it is the only honest evidence that the customer used it.
  useEffect(() => {
    if (product.key === 'battletracker') recordProductOpened('combat_system_pro', 'web');
  }, [product.key]);

  const [usage,       setUsage]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [recentJobs,  setRecentJobs]  = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [animCredits, setAnimCredits] = useState(null);
  const [activeVariants, setActiveVariants] = useState(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm,    setPwForm]    = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError,   setPwError]   = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  useEffect(() => {
    setLoading(true);
    API.get('/v1/usage/current').then(res => setUsage(res.data)).catch(() => null).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setJobsLoading(true);
    API.get('/v1/images/jobs?limit=5')
      .then(res => setRecentJobs(res.data && Array.isArray(res.data.jobs) ? res.data.jobs : []))
      .catch(() => setRecentJobs([]))
      .finally(() => setJobsLoading(false));
  }, []);

  useEffect(() => {
    API.get('/v1/animation/credits').then(res => setAnimCredits(res.data?.balance ?? 0)).catch(() => setAnimCredits(0));
  }, []);

  const hobbyPack      = usage?.hobby_pack || null;
  const hobbyPackBonus = usage?.hobby_pack_bonus || null;
  const isHobbyPack    = !!hobbyPack;
  const isTrial     = !isHobbyPack && (usage?.subscription_status === 'trial' || usage?.subscription_status === 'trialing' || usage?.plan_name === 'Explorer');

  const topupCredits   = usage?.topup_credits || 0;
  const specsUsed      = isHobbyPack ? (hobbyPack.specs_used || 0)  : (usage?.used ?? 0);
  const specsLimit     = isHobbyPack ? hobbyPack.specs_purchased     : (usage?.limit ?? 0);
  const specsRemaining = isHobbyPack ? hobbyPack.specs_remaining     : Math.max(0, specsLimit - specsUsed);
  const usedPct        = specsLimit > 0 ? Math.min(100, Math.round((specsUsed / specsLimit) * 100)) : 0;
  const isHighLimit    = usedPct >= 80 && topupCredits === 0;
  const isAtLimit      = usedPct >= 100 && topupCredits === 0;
  const barColor       = isAtLimit ? '#dc2626' : isHighLimit ? '#f97316' : product.color;

  const planName  = isHobbyPack ? `${hobbyPack.pack_name} Hobby Pack` : (usage?.plan_name || 'Explorer');

  const usageCardLabel = isHobbyPack
    ? 'Hobby Pack Usage'
    : isTrial
      ? 'Trial Allocation'
      : `Monthly ${product.usageLabel}`;

  const usageResetText = isHobbyPack
    ? 'Credits never expire'
    : isTrial
      ? 'Trial allocation - does not reset'
      : usage?.reset_date
        ? `Resets ${new Date(usage.reset_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long' })}`
        : 'Resets monthly';

  const planCardPrimaryLabel = isHobbyPack
    ? 'Manage Pack'
    : isTrial
      ? 'Upgrade Plan'
      : 'Manage Subscription';

  const planCardPrimaryRoute = isHobbyPack
    ? '/manage'
    : isTrial
      ? '/upgrade'
      : '/manage';

  const openPasswordModal  = () => { setPwForm({ current_password: '', new_password: '', confirm_password: '' }); setPwError(''); setPwSuccess(''); setShowPasswordModal(true); };
  const closePasswordModal = () => setShowPasswordModal(false);

  const handlePasswordChange = async () => {
    setPwError(''); setPwSuccess('');
    if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password) { setPwError('All fields are required.'); return; }
    if (pwForm.new_password.length < 8) { setPwError('New password must be at least 8 characters.'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { setPwError('New passwords do not match.'); return; }
    setPwLoading(true);
    try {
      await API.post('/v1/auth/change-password', pwForm);
      setPwSuccess('Password changed successfully.');
      broadcastRevalidate({ reason: 'password-change', scope: 'all' });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { setPwError(err.response?.data?.error || 'Failed to change password. Please try again.'); }
    finally { setPwLoading(false); }
  };

  const isVisual     = product.key === 'visual';
  const isComingSoon = product.comingSoon;
  const isBattlemaps = product.key === 'battlemaps';

  const ph = PRODUCT_HEADERS[product.key] || { title: product.displayName, subtitle: '' };

  const ECOSYSTEM_QUICK_LINKS = [
    { icon: '🗺️', title: 'Explore Battlemaps', sub: 'Browse map packs and tactical systems', route: '/dashboard/battlemaps' },
    { icon: '⚔️', title: 'Open Combat System PRO', sub: 'Run encounters and manage combat',       route: '/dashboard/battletracker' },
  ];

  const allQuickLinks = isBattlemaps
    ? product.quickLinks
    : [...ECOSYSTEM_QUICK_LINKS, ...product.quickLinks];

  function formatJobName(job) {
    if (job.generator_slug) return job.generator_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return 'Generation';
  }
  function getTimeAgo(timestamp) {
    if (!timestamp) return '';
    const now = new Date();
    const localNow = new Date(now.getTime());
    const created = new Date(timestamp);
    const diffMs = localNow - created;
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  }

  function formatJobDate(job) {
    return getTimeAgo(job.completed_at || job.created_at);
  }

  const bottomSectionProps = {
    product, isHobbyPack, isTrial, isBattlemaps, jobsLoading, recentJobs, allQuickLinks,
    navigate, openPasswordModal, formatJobName, formatJobDate, setActiveVariants,
  };

  const BonusCreditsPanel = () => topupCredits > 0 ? (
    <div style={s.bonusCreditsPanel}>
      <div style={{ ...s.cardLabel, color: '#16a34a', marginBottom: 6 }}>Bonus Credits</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 42, fontWeight: 800, color: '#16a34a', lineHeight: 1 }}>{topupCredits}</span>
        <span style={{ fontSize: 14, color: '#9ca3af' }}>credits available</span>
      </div>
      <div style={s.barTrack}><div style={{ ...s.barFill, width: '100%', background: '#16a34a' }} /></div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Used after plan credits are exhausted</div>
    </div>
  ) : null;

  return (
    <div className="vi-portal-dashboard" style={{ ...s.page, ...viThemeVars(product), background: viPageBg(product) }}>
      <GuardedCheckoutConsent checkout={consentCheckout} />
      <DccVerificationOverlay />


      {showPasswordModal && (
        <div style={s.modalOverlay} onClick={closePasswordModal}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}><div style={s.modalTitle}>Change Password</div><button style={s.modalClose} onClick={closePasswordModal}>&#x2715;</button></div>
            <div style={s.modalBody}>
              <div style={s.modalEmailNote}>Account: <strong>{user?.email}</strong></div>
              {pwError   && <div style={s.modalError}>{pwError}</div>}
              {pwSuccess && <div style={s.modalSuccess}>{pwSuccess}</div>}
              {!pwSuccess && (
                <>
                  <div style={s.fieldGroup}><label style={s.fieldLabel}>Current Password</label><input type="password" style={s.fieldInput} value={pwForm.current_password} onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} placeholder="Enter your current password" autoComplete="current-password" /></div>
                  <div style={s.fieldGroup}><label style={s.fieldLabel}>New Password</label><input type="password" style={s.fieldInput} value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} placeholder="At least 8 characters" autoComplete="new-password" /></div>
                  <div style={s.fieldGroup}><label style={s.fieldLabel}>Confirm New Password</label><input type="password" style={s.fieldInput} value={pwForm.confirm_password} onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))} placeholder="Repeat new password" autoComplete="new-password" /></div>
                  <button style={{ ...s.modalBtn, opacity: pwLoading ? 0.6 : 1 }} onClick={handlePasswordChange} disabled={pwLoading}>{pwLoading ? 'Saving...' : 'Change Password'}</button>
                </>
              )}
              {pwSuccess && <button style={s.modalBtn} onClick={closePasswordModal}>Close</button>}
            </div>
          </div>
        </div>
      )}

      <ProductNav />
      {usesGuidedHelp(product.key) ? (
        <HelpEntry pageKey={product.key} />
      ) : (
      <SideHelpPanel title="Dashboard Overview">
        <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 20, overflowY: 'auto' }}>
          <div><span style={hp.heading}>What This Page Is</span><p style={hp.body}>This dashboard is your central hub for accessing all VISINVOKE systems, tracking your usage, and managing your account.</p></div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}><span style={hp.heading}>System Access</span><ul style={hp.list}><li style={hp.item}><span style={hp.bold}>D&amp;D Generators</span> — Creative generation system for fantasy and worldbuilding</li><li style={hp.item}><span style={hp.bold}>Professional Generators</span> — Structured production system for consistent output</li><li style={hp.item}><span style={hp.bold}>Battlemaps</span> — Tactical map generation system</li><li style={hp.item}><span style={hp.bold}>Combat PRO</span> — Manage encounters and gameplay systems</li></ul></div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}><span style={hp.heading}>Your Plan &amp; Usage</span><p style={hp.body}>The plan card shows your current subscription level, while your usage display shows how many credits you have and what remains.</p></div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}><span style={hp.heading}>How To Use This Page</span><ol style={hp.list}><li style={hp.item}>Select a system from the top navigation</li><li style={hp.item}>Enter the generator workspace</li><li style={hp.item}>Configure your selections</li><li style={hp.item}>Generate your image</li></ol></div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}><span style={hp.heading}>Tips</span><ul style={hp.list}><li style={hp.item}>Start with default settings before adding complexity</li><li style={hp.item}>Use each system for its intended purpose for best results</li><li style={hp.item}>Check your remaining credits before large generation runs</li></ul></div>
        </div>
      </SideHelpPanel>
      )}

      <div className="vi-desktop-wide vi-portal-dashboard-container" style={s.container}>

        <div className="vi-portal-hero" style={s.productHeader}>
          <div style={s.productTitleRow}>
            <h1 className="vi-portal-hero-title" style={s.productTitle} data-help-id="header-title">{ph.title}</h1>
            <div style={s.productPillRow}>
              {isTrial && !isComingSoon && (
                <button style={{ ...s.upgradeCta, background: product.accentGradient }} onClick={() => navigate('/upgrade')}>Upgrade to Subscription &rarr;</button>
              )}
              {isHobbyPack && isVisual && (
                <button style={{ ...s.upgradeCta, background: product.accentGradient }} onClick={() => navigate('/upgrade')}>Upgrade to Subscription &rarr;</button>
              )}
            </div>
          </div>
          <p className="vi-portal-hero-subtitle" style={s.productSubtitle}>{ph.subtitle}</p>
          {product.key === 'battletracker' && (
            <p style={s.productCredibility}>Developed as part of the VISINVOKE ecosystem and shaped by over 40 years of Dungeon Master experience, Combat PRO is built for real DND combat, live encounter control and active campaign play.</p>
          )}
        </div>

        {isBattlemaps ? (
          <BattlemapsDashboard
            usage={usage}
            recentJobs={recentJobs}
            jobsLoading={jobsLoading}
            animCredits={animCredits}
            loading={loading}
            navigate={navigate}
            openPasswordModal={openPasswordModal}
          />

        ) : product.key === 'battletracker' ? (
          <BattleTrackerGate user={user} navigate={navigate} />

        ) : isComingSoon ? (
          <div style={s.comingSoonCard}>
            <div style={s.comingSoonIcon}>{product.icon}</div>
            <h2 style={s.comingSoonTitle}>{product.displayName}</h2>
            <p style={s.comingSoonDesc}>{product.displayName} is under active development and will be available soon. Your Visinvoke account will give you access when it launches.</p>
            <div style={s.comingSoonFeatures}>{product.pillsSoon.map((f, i) => (<div key={i} style={s.comingSoonFeature}><span style={{ ...s.featureDot, background: product.color }} />{f}</div>))}</div>
            <button style={{ ...s.comingSoonBtn, background: product.accentGradient }} onClick={() => navigate(product.browseRoute)}>{product.browseButtonLabel}</button>
          </div>

        ) : (
          <>
            {loading ? (
              <div style={s.loadingCard}>Loading your account...</div>
            ) : (
              <>
                {(() => {
                  const validJob = !jobsLoading && recentJobs.find(j => isValidLastBuild(j));
                  const dest = validJob ? resolveLastBuildDestination(validJob) : null;
                  if (!validJob || !dest) return null;
                  return (
                    <div className="vi-portal-continue-card" style={s.continueLastCard} data-help-id="continue-card">
                      <div className="vi-portal-continue-left" style={s.continueLastLeft}>
                        <img className="vi-portal-continue-thumb" src={validJob.result.url} alt="" style={s.continueLastThumb} data-help-id="continue-thumb" />
                        <div>
                          <div style={s.continueLastLabel}>Continue Your Last Build</div>
                          <div style={s.continueLastHint}>Resume where you left off</div>
                          <div style={s.continueLastName}>{formatJobName(validJob)}</div>
                          <div style={s.continueLastTime}>{formatJobDate(validJob)}</div>
                        </div>
                      </div>
                      <div className="vi-portal-continue-actions" style={s.continueLastActions}>
                        <style>{DASHBOARD_BTN_CSS}</style>
                        <button data-help-id="continue-primary" className="vi-dash-primary" style={s.continueLastBtn} onClick={() => { viWriteRehydrate(validJob, viDetectIntent(validJob) === 'variation'); navigate(dest.route, { state: { slug: dest.slug } }); }}>{viIntentLabel(validJob)} &rarr;</button>
                        <button data-help-id="continue-workspace" className="vi-dash-secondary" style={s.continueLastSecBtn} onClick={() => navigate('/workspace')}>Open Workspace</button>
                        <button data-help-id="continue-browse" className="vi-dash-secondary" style={s.continueLastSecBtn} onClick={() => navigate('/generators')}>Browse Generators</button>
                      </div>
                    </div>
                  );
                })()}

                <div className="vi-portal-dashboard-grid" style={s.topGrid}>
                  <div className="vi-card-hover vi-portal-dashboard-card" style={s.card}>
                    <div className="vi-portal-account-row" style={s.acctRow}>
                      <div className="vi-portal-plan-column" style={s.planCol} data-help-id="plan-card">
                        <div className="vi-portal-plan-top" style={s.planColTop}>
                          <div style={{ ...s.cardLabel, color: product.color, fontSize: 12 }}>{product.planLabel}</div>
                          <div className="vi-portal-plan-name" style={s.planNameLg} data-help-id="plan-name">{planName}</div>
                          {isHobbyPack ? <div style={s.hobbyBadge}><span style={{ ...s.hobbyBadgeDot, background: product.color }} />One-Time Purchase</div> : <div style={s.statusRow} data-help-id="plan-status"><span style={{ ...s.statusDot, background: isTrial ? '#f97316' : '#10b981' }} /><span style={s.statusText}>{isTrial ? 'Trial' : 'Active'}</span></div>}
                          {isHobbyPack ? (
                            <div style={s.hobbyInfoBox}>
                              <div style={s.hobbyInfoRow}><span style={{ ...s.hobbyInfoIcon, color: '#16a34a' }}>&#x2713;</span><span>Access to all live generators</span></div>
                              <div style={s.hobbyInfoRow}><span style={{ ...s.hobbyInfoIcon, color: '#16a34a' }}>&#x2713;</span><span>Credits never expire</span></div>
                              <div style={{ ...s.hobbyInfoRow, color: '#dc2626' }}><span style={{ ...s.hobbyInfoIcon, color: '#dc2626' }}>&#x2715;</span><span>Additional Credits not available</span></div>
                            </div>
                          ) : (
                            <p className="vi-portal-plan-description" style={s.planDescLg}>{isTrial ? 'Full generator access. Up to 10 credits - one-time trial - no monthly reset.' : `Full ${product.displayName} access.`}</p>
                          )}
                        </div>
                        <div style={s.cardActions}>
                          <button data-help-id="plan-manage" className="vi-btn-primary" style={s.btnPrimary} onClick={() => navigate(planCardPrimaryRoute)}>{planCardPrimaryLabel}</button>
                          {(isTrial || isHobbyPack) && <button data-help-id="plan-viewplans" className="vi-btn-primary" style={s.btnSecondary} onClick={() => navigate('/upgrade')}>View Plans</button>}
                        </div>
                      </div>

                      <div className="vi-portal-animation-column" style={s.animCol} data-help-id="anim-credits">
                        <div className="vi-portal-animation-top" style={s.animColTop}>
                          <div style={s.animColHead}>
                            <div style={s.animIcon}>&#9654;</div>
                            <div className="vi-portal-animation-title" style={s.animTitle}>Animation Credits</div>
                            <span style={s.animBalanceSmall} data-help-id="anim-count">{animCredits === null ? '-' : animCredits} credits</span>
                          </div>
                          <div className="vi-portal-animation-description" style={s.animSub}>Animate any generated image. Credits are consumed per animation.</div>
                        </div>
                        <button data-help-id="anim-buy" className="vi-anim-btn" style={s.animBtn} onClick={() => navigate('/animation-credits')}>Buy Animation Credits</button>
                      </div>
                    </div>
                  </div>

                  <div className="vi-card-hover vi-portal-dashboard-card" style={s.card}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                      <div>
                        <div style={{ ...s.cardLabel, color: product.color }} data-help-id="credits-card">{usageCardLabel}</div>
                        <div className="vi-portal-usage-row" style={s.usageRow}><span className="vi-portal-usage-value" style={{ ...s.usageBig, color: barColor }} data-help-id="credits-used">{specsUsed}</span><span style={s.usageOf} data-help-id="credits-limit">/ {specsLimit}</span><span style={s.usageUnit}>credits used</span></div>
                        <div style={s.barTrack}><div className="vi-bar-live" style={{ ...s.barFill, width: `${usedPct}%`, ...vgBarStyle(barColor) }} /></div>
                        <div style={s.usageMeta}>
                          <span style={s.metaText} data-help-id="credits-reset">{usageResetText}</span>
                          <span style={{ ...s.metaText, color: barColor, fontWeight: 700 }} data-help-id="credits-remaining">{specsRemaining} remaining</span>
                        </div>
                        {isAtLimit && <div style={s.alertRed}>All credits used. Upgrade to continue.</div>}
                        {isHighLimit && !isAtLimit && <div style={s.alertOrange}>Running low - {specsRemaining} credits left.</div>}
                        {!isAtLimit && usedPct >= 75 && !isHighLimit && <div style={s.convHint}>You&apos;re nearing your monthly limit — upgrading keeps your workflow uninterrupted.</div>}
                        {!isAtLimit && usedPct >= 90 && <div style={s.convHintStrong}>Almost out of builds — upgrading gives significantly more capacity.</div>}
                        {isHighLimit && topupCredits > 0 && !isTrial && (<div style={s.convHintRow}><span style={s.convHint}>Top-ups are available, but upgrading provides significantly better value per build.</span><button style={s.upgradeBtn} onClick={() => navigate('/upgrade')}>Upgrade</button></div>)}
                        {!isHobbyPack && !isTrial && specsLimit > 0 && <div style={s.convCapacity}>Current plan capacity: {specsLimit} builds per cycle &mdash; higher tiers increase this significantly.</div>}
                        {!isHobbyPack && !isTrial && <div style={s.convGrandfathered}>As an early member, your original plan pricing stays protected as VISINVOKE grows. New generators, features, and improvements keep expanding the platform, so your membership continues to gain value over time &mdash; and you&apos;re never moved onto a newer, higher-priced tier just because more is added.</div>}
                        {isHobbyPack && (
                          <div style={s.hobbyUpgradeNudge}>
                            <span style={{ fontSize: 13, color: '#fbbf24' }}>&#x1F4A1; <strong>Better value:</strong> A subscription gives you monthly credits from $9.99/mo. Access all VISINVOKE systems.</span>
                            <button style={s.nudgeBtn} onClick={() => navigate('/upgrade')}>Compare Plans</button>
                          </div>
                        )}
                      </div>
                      {topupCredits > 0 && (
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16, marginTop: 8 }} data-help-id="bonus-credits">
                          <div style={{ ...s.cardLabel, color: '#16a34a', marginBottom: 6 }}>Bonus Credits</div>
                          <div style={s.usageRow}><span style={{ ...s.usageBig, color: '#16a34a' }}>{topupCredits}</span><span style={s.usageUnit}>credits available</span></div>
                          <div style={s.barTrack}><div className="vi-bar-live" style={{ ...s.barFill, width: '100%', ...vgBarStyle('#16A34A','#35D17F','#0F7A37') }} /></div>
                          <div style={{ ...s.usageMeta }}><span style={s.metaText}>Used after plan credits are exhausted</span></div>
                        </div>
                      )}
                      {hobbyPackBonus && hobbyPackBonus.specs_remaining > 0 && (
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16 }}>
                          <div style={{ ...s.cardLabel, color: '#d97706', marginBottom: 6 }}>Pack Credits</div>
                          <div style={s.usageRow}><span style={{ ...s.usageBig, color: '#d97706' }}>{hobbyPackBonus.specs_remaining}</span><span style={s.usageUnit}>credits available</span></div>
                          <div style={s.barTrack}><div className="vi-bar-live" style={{ ...s.barFill, width: '100%', ...vgBarStyle('#D97706','#F59A23','#A85B0A') }} /></div>
                          <div style={{ ...s.usageMeta }}><span style={s.metaText}>{hobbyPackBonus.pack_name} Pack &mdash; used after subscription credits</span></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <BottomSection {...bottomSectionProps} />
              </>
            )}
          </>
        )}
      </div>
      {activeVariants && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999 }} onClick={() => setActiveVariants(null)}>
          <div style={{ position: 'absolute', top: '5%', left: '5%', width: '90%', height: '90%', background: '#0b1220', borderRadius: 12, padding: 16, overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: '#fff', margin: 0 }}>{activeVariants.generator_slug}</h3>
              <button style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }} onClick={() => setActiveVariants(null)}>Close</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {(activeVariants.versions || []).map((v, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {activeVariants.result?.url && <img src={activeVariants.result.url} alt={`v${i+1}`} style={{ width: '100%', borderRadius: 6, objectFit: 'cover' }} />}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>v{i + 1}</span>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{getTimeAgo(v.created_at)}</span>
                  </div>
                  <button style={{ background: 'var(--vi-cta)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 0', fontWeight: 700, fontSize: 11, cursor: 'pointer' }} onClick={() => { loadVersion(activeVariants, i); setActiveVariants(null); }}>
                    Continue Build
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(180deg, #0f0e16 0%, #15141c 100%)', fontFamily: "'Segoe UI', sans-serif" },
  container: { maxWidth: 1400, margin: '0 auto', padding: '28px 40px' },
  productIndicator: { display: 'flex', alignItems: 'center', gap: 8, borderRadius: 20, padding: '6px 14px', color: '#fff', fontWeight: 700, fontSize: 14 },
  productHeader: { textAlign: 'center', marginBottom: 28 },
  productTitleRow: { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  productPillRow: { position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 12 },
  productTitle: { fontSize: 52, fontWeight: 900, color: '#ffffff', lineHeight: 1.1, margin: 0, letterSpacing: '-1px' },
  productSubtitle: { fontSize: 18, fontWeight: 500, color: '#cbd5e1', lineHeight: 1.5, margin: '12px 0 0' },
  productCredibility: { fontSize: 14, fontWeight: 400, color: '#94a3b8', lineHeight: 1.6, margin: '10px 0 0', textAlign: 'center' },
  upgradeCta: { color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700, fontSize: 15, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 4px 15px rgba(0,0,0,0.3)', transition: 'filter 0.16s ease, transform 0.14s cubic-bezier(0.22,1,0.36,1), box-shadow 0.16s ease' },
  loadingCard: { background: 'rgba(255,255,255,0.1)', borderRadius: 16, padding: 40, textAlign: 'center', color: '#fff' },
  comingSoonCard: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '48px 40px', textAlign: 'center', maxWidth: 600, margin: '0 auto' },
  comingSoonIcon: { fontSize: 64, marginBottom: 16 },
  comingSoonTitle: { color: '#fff', fontSize: 28, fontWeight: 800, margin: '0 0 12px' },
  comingSoonDesc: { color: 'rgba(255,255,255,0.6)', fontSize: 16, lineHeight: 1.7, margin: '0 0 28px' },
  comingSoonFeatures: { display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start', maxWidth: 320, margin: '0 auto 32px', textAlign: 'left' },
  comingSoonFeature: { display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,0.8)', fontSize: 14 },
  featureDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  comingSoonBtn: { color: '#fff', border: 'none', borderRadius: 10, padding: '13px 28px', fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  topGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 },
  bottomGrid: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, alignItems: 'stretch' },
  leftCol: { display: 'flex', flexDirection: 'column', gap: 20 },
  animCard: { background: 'linear-gradient(158deg, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0.03) 58%, rgba(255,255,255,0.012) 100%), var(--vi-s1, #15141c)', borderRadius: 16, padding: '16px 24px', boxShadow: '0 14px 36px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginBottom: 24, flexWrap: 'wrap', border: '1px solid rgba(34,197,94,0.24)' },
  animLeft: { display: 'flex', alignItems: 'center', gap: 16, flex: 1 },
  animIcon: { width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg, #15803d, #16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, fontWeight: 700 },
  animTitle: { fontWeight: 800, fontSize: 20, color: '#f4f4f8', marginBottom: 3 },
  animSub: { fontSize: 18, color: 'rgba(255,255,255,0.64)', lineHeight: 1.5 },
  animRight: { display: 'flex', alignItems: 'center', gap: 20, flexShrink: 0 },
  animBalance: { display: 'flex', alignItems: 'baseline', gap: 6 },
  animBalanceNum: { fontWeight: 900, fontSize: 36, color: '#f4f4f8', lineHeight: 1 },
  animBalanceLabel: { fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 600 },
  animBtn: { ...vgGlass('#0F7A37','#16A34A','#35D17F'), borderRadius: 10, padding: '11px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  acctRow: { flex: 1, display: 'flex', alignItems: 'stretch', flexWrap: 'wrap' },
  planCol: { flex: 1, minWidth: 240, boxSizing: 'border-box', paddingRight: 24, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 18 },
  planColTop: { display: 'flex', flexDirection: 'column', gap: 16 },
  planNameLg: { fontWeight: 800, fontSize: 36, color: '#f4f4f8', lineHeight: 1.1 },
  planDescLg: { fontSize: 19, color: 'rgba(255,255,255,0.68)', lineHeight: 1.55, margin: 0 },
  animCol: { flex: 1, minWidth: 240, boxSizing: 'border-box', paddingLeft: 24, borderLeft: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 18 },
  animColTop: { display: 'flex', flexDirection: 'column', gap: 16 },
  animColHead: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  animBalanceSmall: { fontSize: 18, color: 'rgba(255,255,255,0.6)', fontWeight: 700 },
  bmExplain: { fontSize: 'clamp(14px, 1.2vw, 16px)', color: 'rgba(255,255,255,0.66)', lineHeight: 1.6, marginTop: 14, width: '100%' },
  bmPackList: { marginTop: 16, width: '100%', display: 'flex', flexDirection: 'column', gap: 6 },
  bmPackListHead: { fontSize: 'clamp(13px, 1.1vw, 15px)', fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: 4 },
  bmPackRow: { fontSize: 'clamp(14px, 1.15vw, 16px)', color: 'rgba(255,255,255,0.64)', lineHeight: 1.55 },
  bmAccessName: { fontWeight: 800, fontSize: 'clamp(20px, 2vw, 27px)', color: '#f4f4f8', lineHeight: 1.15 },
  bmAccessDesc: { fontSize: 'clamp(13px, 1.05vw, 15px)', color: 'rgba(255,255,255,0.66)', lineHeight: 1.55, margin: 0 },
  bmAccessStatus: { fontSize: 'clamp(14px, 1.1vw, 16px)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, textTransform: 'capitalize' },
  card: { background: 'linear-gradient(158deg, rgba(var(--vi-acc, 163, 71, 255), 0.06) 0%, rgba(var(--vi-acc, 163, 71, 255), 0.018) 55%, rgba(255,255,255,0.014) 100%), var(--vi-s1, #15141c)', borderRadius: 16, padding: 28, border: '1px solid rgba(var(--vi-acc, 163, 71, 255), 0.16)', boxShadow: '0 18px 44px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: 12, transform: 'scale(1.01)', transformOrigin: 'top center', transition: 'transform 0.2s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s ease' },
  cardLabel: { fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' },
  planName: { fontWeight: 800, fontSize: 24, color: '#f4f4f8' },
  statusRow: { display: 'flex', alignItems: 'center', gap: 8 },
  statusDot: { width: 14, height: 14, borderRadius: '50%', flexShrink: 0 },
  statusText: { fontSize: 19, color: 'rgba(255,255,255,0.9)', fontWeight: 700, textTransform: 'capitalize' },
  hobbyBadge: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: '#fbbf24' },
  hobbyBadgeDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  hobbyInfoBox: { background: 'linear-gradient(158deg, rgba(212,175,55,0.10), rgba(212,175,55,0.03)), var(--vi-s2, #1c1b25)', border: '1px solid rgba(212,175,55,0.28)', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 },
  hobbyInfoRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.78)' },
  hobbyInfoIcon: { fontWeight: 700, fontSize: 13, flexShrink: 0 },
  planDesc: { fontSize: 14, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 },
  cardActions: { display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 },
  btnPrimary: { ...VG_CAT, borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  btnSecondary: { ...VG_CAT, borderRadius: 8, padding: '9px 17px', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  usageRow: { display: 'flex', alignItems: 'baseline', gap: 6 },
  usageBig: { fontSize: 42, fontWeight: 800, lineHeight: 1 },
  usageOf: { fontSize: 20, color: '#9ca3af', fontWeight: 600 },
  usageUnit: { fontSize: 14, color: '#9ca3af' },
  barTrack: { height: 10, background: 'rgba(var(--vi-primary-rgb, 163,71,255),0.12)', borderRadius: 5, overflow: 'visible', boxShadow: 'inset 0 0 8px rgba(var(--vi-primary-rgb, 163,71,255),0.25), inset 0 1px 2px rgba(0,0,0,0.4)' },
  barFill: { height: '100%', borderRadius: 5, transition: 'width 0.7s cubic-bezier(0.22,1,0.36,1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)' },
  usageMeta: { display: 'flex', justifyContent: 'space-between' },
  metaText: { fontSize: 13, color: '#9ca3af' },
  alertRed: { background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.35)', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  alertOrange: { background: 'rgba(234,88,12,0.12)', border: '1px solid rgba(234,88,12,0.35)', color: '#fdba74', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  hobbyUpgradeNudge: { background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.38)', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  nudgeBtn: { background: '#D4AF37', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  bonusCreditsPanel: { borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 24, flex: 1 },
  ecosystemContextLine: { color: 'rgba(255,255,255,0.45)', fontSize: 13, marginBottom: 16, marginTop: 4, fontStyle: 'italic' },
  ecosystemSection: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '28px 28px 20px', marginBottom: 24 },
  ecosystemHeader: { marginBottom: 20 },
  ecosystemTitle: { color: '#fff', fontWeight: 800, fontSize: 20, marginBottom: 6 },
  ecosystemSub: { color: 'rgba(255,255,255,0.5)', fontSize: 13 },
  ecosystemGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 16, alignItems: 'stretch' },
  ecosystemCard: { background: 'linear-gradient(158deg, rgba(var(--vi-acc, 163, 71, 255), 0.06) 0%, rgba(255,255,255,0.012) 100%), var(--vi-s2, #1c1b25)', borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', height: '100%', border: '1px solid rgba(var(--vi-acc, 163, 71, 255), 0.14)', boxShadow: '0 10px 28px rgba(0,0,0,0.4)', opacity: 0.97, transition: 'transform 0.18s cubic-bezier(0.22,1,0.36,1), box-shadow 0.18s ease, opacity 0.18s ease' },
  ecosystemCardTop: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
  ecosystemCardIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 },
  ecosystemCardMeta: { display: 'flex', flexDirection: 'column', gap: 4 },
  ecosystemCardTitle: { fontWeight: 800, fontSize: 15, color: '#f4f4f8' },
  ecosystemLiveBadge: { display: 'inline-block', background: 'rgba(34,197,94,0.16)', color: '#4ade80', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 20, alignSelf: 'flex-start' },
  ecosystemSoonBadge: { display: 'inline-block', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.55)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 20, alignSelf: 'flex-start' },
  ecosystemCardDesc: { fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55, margin: '0 0 10px' },
  ecosystemPoints: { listStyle: 'none', margin: '0 0 10px', padding: 0, display: 'flex', flexDirection: 'column', gap: 6, minHeight: 88 },
  ecosystemPoint: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'rgba(255,255,255,0.72)' },
  ecosystemPointDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  ecosystemCtaWrap: { marginTop: 'auto', paddingTop: 4 },
  ecosystemCta: { color: '#fff', border: 'none', borderRadius: 8, padding: '11px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', width: '100%', boxShadow: '0 3px 10px rgba(0,0,0,0.18)', transition: 'filter 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease', letterSpacing: '0.01em', height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' },
  scaleSignal: { color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', fontStyle: 'italic', letterSpacing: '0.02em' },
  cardDesc: { fontSize: 14, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 },
  pills: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  pill: { background: 'rgba(var(--vi-acc, 163, 71, 255), 0.14)', color: '#fff', border: '1px solid rgba(var(--vi-acc, 163, 71, 255), 0.3)', borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 600 },
  pillComingSoon: { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 600 },
  browseBtn: { ...VG_CAT, borderRadius: 10, padding: '12px 24px', fontWeight: 700, fontSize: 15, cursor: 'pointer', alignSelf: 'flex-start', marginTop: 4 },
  quickLinks: { display: 'flex', flexDirection: 'column', gap: 8 },
  quickLink: { ...VG_CAT, boxShadow: 'inset 0 0 22px rgba(var(--vi-primary-rgb),0.20), inset 0 1px 0 rgba(255,255,255,0.22), 0 6px 16px rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', gap: 14, borderRadius: 10, padding: '12px 16px', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'filter 0.15s ease, transform 0.18s cubic-bezier(0.22,1,0.36,1)' },
  quickIcon: { fontSize: 22, flexShrink: 0 },
  quickTitle: { fontWeight: 700, fontSize: 14, color: '#eef0f5' },
  quickSub: { fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 },
  workspaceCard: { background: 'linear-gradient(135deg, var(--vi-s2) 0%, var(--vi-s1) 100%)', borderRadius: 16, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: '1px solid rgba(var(--vi-acc),0.3)' },
  workspaceCardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 },
  workspaceCardLeft: { display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1, minWidth: 0 },
  workspaceIcon: { fontSize: 28, flexShrink: 0, marginTop: 2 },
  workspaceTitle: { fontWeight: 800, fontSize: 17, color: '#fff', marginBottom: 5 },
  workspaceDesc: { fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, maxWidth: 520 },
  workspaceBtnGroup: { display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0, alignItems: 'flex-end' },
  workspaceHintBox: { marginTop: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  workspaceHintText: { fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: 'rgba(255,255,255,0.88)' },
  workspaceOpenBtn: { ...VG_CAT, borderRadius: 8, padding: '9px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  workspaceUpgradeBtn: { ...VG_CAT, borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  workspaceHint: { fontSize: 12, color: 'rgba(255,255,255,0.62)', marginTop: 6, lineHeight: 1.4 },
  recentBuildsEmpty: { fontSize: 13, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' },
  recentBuildsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, padding: '0 0 4px' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal: { background: 'var(--vi-s1, #15141c)', borderRadius: 16, width: '100%', maxWidth: 440, border: '1px solid rgba(var(--vi-acc, 163, 71, 255), 0.2)', boxShadow: '0 24px 70px rgba(0,0,0,0.6)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0' },
  modalTitle: { fontWeight: 800, fontSize: 20, color: '#f4f4f8' },
  modalClose: { background: 'none', border: 'none', fontSize: 18, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4, lineHeight: 1 },
  modalBody: { padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 },
  modalEmailNote: { background: 'rgba(var(--vi-acc, 163, 71, 255), 0.1)', border: '1px solid rgba(var(--vi-acc, 163, 71, 255), 0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'rgba(255,255,255,0.78)' },
  modalError: { background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.35)', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  modalSuccess: { background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.35)', color: '#86efac', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel: { fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.78)' },
  fieldInput: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', color: '#f4f4f8' },
  modalBtn: { background: 'var(--vi-cta)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 4 },
  continueLastCard: { background: 'linear-gradient(158deg, rgba(var(--vi-acc, 163, 71, 255), 0.08) 0%, rgba(var(--vi-acc, 163, 71, 255), 0.02) 60%, rgba(255,255,255,0.014) 100%), var(--vi-s1, #15141c)', borderRadius: 16, padding: '22px 28px', boxShadow: '0 16px 44px rgba(0,0,0,0.45), 0 0 0 1px rgba(var(--vi-acc, 163, 71, 255), 0.18)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, marginBottom: 24, flexWrap: 'wrap', borderTop: '3px solid rgba(var(--vi-acc, 163, 71, 255), 0.85)' },
  continueLastLeft: { display: 'flex', alignItems: 'center', gap: 16, flex: 1 },
  continueLastThumb: { width: 56, height: 56, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '2px solid rgba(var(--vi-acc, 163, 71, 255), 0.4)' },
  continueLastLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.5)', marginBottom: 3 },
  continueLastName: { fontWeight: 800, fontSize: 15, color: '#f4f4f8', marginBottom: 2 },
  continueLastTime: { fontSize: 12, color: 'rgba(255,255,255,0.45)' },
  continueLastActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 },
  continueLastBtn: { ...VG_CAT, borderRadius: 8, padding: '11px 22px', fontWeight: 800, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.01em', transition: 'filter 0.14s, transform 0.12s, box-shadow 0.14s' },
  continueLastSecBtn: { ...VG_CAT, borderRadius: 8, padding: '9px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'filter 0.14s, transform 0.12s', opacity: 0.92 },
  continueLastHint: { fontSize: 11, color: '#b5b9c4', fontWeight: 400, marginBottom: 1 },
  convHintRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 },
  upgradeBtn: { fontSize: 11, padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.75)', cursor: 'pointer', marginLeft: 10, flexShrink: 0 },
  convHint: { fontSize: 12, color: 'rgba(255,200,60,0.7)', lineHeight: 1.5 },
  convHintStrong: { fontSize: 12, color: 'rgba(255,160,60,0.85)', marginTop: 6, lineHeight: 1.5, fontWeight: 600 },
  convCapacity: { fontSize: 11, color: 'rgba(255,255,255,0.42)', marginTop: 5, lineHeight: 1.5 },
  convGrandfathered: { fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 8, lineHeight: 1.6, width: '100%' },
  momentumHint: { fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 12, fontStyle: 'italic', paddingLeft: 2 },
  bmEngineStrip: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20, padding: '22px 28px', marginBottom: 28, background: 'rgba(212,175,55,0.07)', border: '1px solid rgba(212,175,55,0.22)', borderRadius: 16 },
  bmEngineStripLeft: { flex: 1, minWidth: 280 },
  bmEngineStripLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#D4AF37', marginBottom: 8 },
  bmEngineStripTitle: { fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 6, lineHeight: 1.4 },
  bmEngineStripSub: { fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55 },
  bmEngineStripRight: { display: 'flex', gap: 10, flexShrink: 0, flexWrap: 'wrap' },
  bmEngineStripPrimary: { background: 'linear-gradient(135deg, #D4AF37, #b8962e)', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(212,175,55,0.3)' },
  bmEngineStripSecondary: { background: 'transparent', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.4)', borderRadius: 8, padding: '11px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
};

const ast = {
  panel: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '20px 24px', marginBottom: 24 },
  panelHeader: { marginBottom: 16 },
  panelTitle: { color: '#fff', fontWeight: 800, fontSize: 16 },
  statusBlock: { marginBottom: 16 },
  statusRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  statusDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  statusLabel: { color: '#fff', fontWeight: 700, fontSize: 14 },
  restrictionTag: { background: 'rgba(249,115,22,0.15)', color: '#f97316', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' },
  messageBox: { border: '1px solid', borderRadius: 10, padding: '12px 16px' },
  messageLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 },
  messageText: { fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 },
  historySection: { borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 },
  historyTitle: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 },
  historyEmpty: { color: 'rgba(255,255,255,0.3)', fontSize: 13, fontStyle: 'italic' },
  historyList: { display: 'flex', flexDirection: 'column', gap: 8 },
  historyItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '10px 14px' },
  historyItemLeft: { flex: 1 },
  historyAction: { color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: 13, marginBottom: 2 },
  historyNote: { color: 'rgba(255,255,255,0.55)', fontSize: 12, lineHeight: 1.5 },
  historyDate: { color: 'rgba(255,255,255,0.35)', fontSize: 12, flexShrink: 0 },
};

const hp = {
  heading: { fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 0, marginBottom: 8, display: 'block' },
  body: { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, margin: '0 0 10px 0' },
  list: { margin: 0, padding: '0 0 4px 16px', display: 'flex', flexDirection: 'column', gap: 0 },
  item: { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 6, borderRadius: 6, padding: '2px 4px', transition: 'background 0.15s' },
  bold: { fontWeight: 700, color: 'rgba(255,255,255,0.88)' },
  divider: { height: 1, background: 'rgba(255,255,255,0.05)', margin: '12px 0' },
};
